// src/types/scales.ts
function isFlatPageScale(entry) {
  return entry.type === "ratio" || entry.type === "calibration";
}
function normalizePageScales(entry) {
  if (isFlatPageScale(entry)) {
    return { default: entry, viewports: [] };
  }
  return { default: entry.default, viewports: entry.viewports ?? [] };
}

// src/pages.ts
function pageIdOf(documentId, nativeKey) {
  return `${documentId}:${nativeKey}`;
}
function entries(manifest) {
  const pages = manifest?.pages;
  return Array.isArray(pages) ? pages : [];
}
function pageEntryOf(manifest, page) {
  return entries(manifest).find((p) => p?.page === page);
}
function pageSourceOf(manifest, page) {
  return pageEntryOf(manifest, page)?.source;
}
function sourceDocumentIds(manifest) {
  const seen = /* @__PURE__ */ new Set();
  for (const p of entries(manifest)) {
    const id = p?.source?.document_id;
    if (typeof id === "string" && id) seen.add(id);
  }
  return [...seen];
}
function scopedElementKey(documentId, elementId) {
  return `${documentId ?? ""}\0${elementId}`;
}
function elementScope(element, manifest) {
  const ids = sourceDocumentIds(manifest);
  if (ids.length === 0) return void 0;
  if (typeof element.document_id === "string" && element.document_id) return element.document_id;
  return ids.length === 1 ? ids[0] : void 0;
}
function elementKey(element, manifest) {
  return scopedElementKey(elementScope(element, manifest), element.id);
}
function pagedElementKey(page, elementId, manifest) {
  const ids = sourceDocumentIds(manifest);
  if (ids.length === 0) return scopedElementKey(void 0, elementId);
  return scopedElementKey(pageSourceOf(manifest, page)?.document_id, elementId);
}
function indexElements(elements, manifest) {
  const byKey = /* @__PURE__ */ new Map();
  for (const el of elements) byKey.set(elementKey(el, manifest), el);
  return byKey;
}
var PAGED_ARRAYS = {
  "marks.json": "marks",
  "annotations.json": "annotations",
  "model3d.json": "models",
  "schedules.json": "schedules",
  "titleblock.json": "sheets"
};
function recordPagesOf(name, doc) {
  if (!doc || typeof doc !== "object") return [];
  const out = [];
  if (name === "scales.json") {
    const pages = doc.pages;
    if (pages && typeof pages === "object") {
      for (const key2 of Object.keys(pages)) {
        const page = Number(key2);
        if (Number.isInteger(page)) out.push({ page, where: `scales.json/pages/${key2}` });
      }
    }
    return out;
  }
  const key = PAGED_ARRAYS[name];
  if (!key) return out;
  const arr = doc[key];
  if (!Array.isArray(arr)) return out;
  arr.forEach((rec, i) => {
    const page = rec?.page;
    if (typeof page === "number") out.push({ page, where: `${name}/${key}/${i}` });
  });
  return out;
}
function checkPagesInvariants(doc) {
  const pages = entries(doc);
  const errors = [];
  const seenPage = /* @__PURE__ */ new Set();
  const seenId = /* @__PURE__ */ new Set();
  for (let i = 0; i < pages.length; i++) {
    const p = pages[i];
    if (seenPage.has(p.page)) errors.push(`pages[${i}].page ${p.page} is duplicated`);
    seenPage.add(p.page);
    if (seenId.has(p.page_id)) errors.push(`pages[${i}].page_id "${p.page_id}" is duplicated`);
    seenId.add(p.page_id);
    const expected = pageIdOf(p.source.document_id, p.native_key);
    if (p.page_id !== expected) {
      errors.push(`pages[${i}].page_id "${p.page_id}" must be source.document_id:native_key ("${expected}")`);
    }
  }
  for (let n = 1; n <= pages.length; n++) {
    if (!seenPage.has(n)) {
      errors.push(`pages must be contiguous 1..${pages.length}: page ${n} is missing`);
      break;
    }
  }
  return errors;
}
function checkPackagePages(manifest, docs) {
  const errors = [];
  const pages = new Set(entries(manifest).map((p) => p.page));
  const sources = sourceDocumentIds(manifest);
  const known = new Set(sources);
  for (const [name, doc] of Object.entries(docs)) {
    const orphans = recordPagesOf(name, doc).filter((r) => !pages.has(r.page));
    if (orphans.length > 0) {
      const shown = orphans.slice(0, 5).map((r) => `${r.where} (page ${r.page})`).join(", ");
      const more = orphans.length > 5 ? ` and ${orphans.length - 5} more` : "";
      errors.push(`${name}: ${orphans.length} record(s) on a page pages.json does not list: ${shown}${more}`);
    }
  }
  const elements = docs["elements.json"]?.elements;
  if (Array.isArray(elements)) {
    const unscoped = [];
    const unknownDoc = [];
    for (const el of elements) {
      const id = String(el?.id);
      if (typeof el?.document_id === "string") {
        if (!known.has(el.document_id)) unknownDoc.push(`${id} (${el.document_id})`);
      } else if (sources.length > 1) {
        unscoped.push(id);
      }
    }
    if (unscoped.length > 0) {
      errors.push(
        `elements.json: ${unscoped.length} element(s) lack document_id but pages.json lists ${sources.length} source documents (${sources.join(", ")}): ${fmtIds(unscoped)}`
      );
    }
    if (unknownDoc.length > 0) {
      errors.push(`elements.json: ${unknownDoc.length} element(s) name a document_id pages.json does not list: ${fmtIds(unknownDoc)}`);
    }
  }
  return errors;
}
function fmtIds(ids) {
  return ids.length <= 5 ? ids.join(", ") : `${ids.slice(0, 5).join(", ")} and ${ids.length - 5} more`;
}

// src/types/annotations.ts
var ANNOTATION_TYPES = [
  "polygon",
  "polyline",
  "rectangle",
  "circle",
  "text",
  "arrow",
  "callout",
  "cloud",
  "stamp",
  "highlight",
  "freehand",
  "count",
  "dimension",
  "tag",
  "detail",
  "other"
];
var CANVAS_ONLY_MARKUP_TYPES = ["line", "ellipse", "group", "snapshot", "move", "image", "photo-pin"];
var MARKUP_TYPES = [
  "line",
  "polyline",
  "polygon",
  "rectangle",
  "ellipse",
  "freehand",
  "arrow",
  "highlight",
  "cloud",
  "callout",
  "count",
  "stamp",
  "text",
  "group",
  "dimension",
  "snapshot",
  "move",
  "image",
  "photo-pin",
  "tag",
  "detail",
  "other"
];
var MARKUP_MOVE_CONSTRAINTS = ["free", "axis", "host"];
var MEDIA_MAX_ITEMS = 24;
var MEDIA_MIMES = ["image/jpeg", "image/png"];
var MEDIA_SOURCE_KINDS = ["paste", "clipboard", "drop", "file", "camera", "pdf-import"];
var ROOM_SOURCES = ["auto", "typed"];
var CANVAS_TO_ANNOTATION_TYPE = {
  line: "polyline",
  // 2-point polyline
  ellipse: "circle",
  // canvas ellipse = bim-pdf circle
  move: "arrow"
  // element move = its arrow (old centre -> new centre)
  // group: no direct mapping -- flatten children before emitting
};

// src/markups/legacy-review-fields.json
var legacy_review_fields_default = {
  $comment: "R6-4 (2026-10-09): the markup review loop v8 is deleted; every project is plain ink unless the request store's gate is on (review-request-contract.json). Markups written before then may still carry the v8 review fields below. They stay READABLE (the SPA's MarkupSchema parses them) and FROZEN: no client creates or changes one, on any project -- firestore.rules (helper legacyInkFields(), generated by apps/pdfmarkuptool/scripts/gen-rules-review-request.mjs), the local server's guard (local/src/opsRules.ts) and the SPA's write path (services/markupFields.ts) all read this list. The other frozen fields are the request contract's clientWrites.inkFields (request_id, the R2 lock dispatch_id + released, trace_id) and clientWrites.inkStateFields (status, status_history: a client create carries at most the fresh state -- status `freshStatus`, an empty history -- and no update changes them). The service (Admin SDK) still writes needs_human_reason when review.ingest / document.upsert parks a markup (review-core-contract.json ingest.markups).",
  freshStatus: "open",
  fields: [
    "request",
    "review",
    "delta",
    "contractVersion",
    "in_reply_to",
    "addresses",
    "sheet_anchor",
    "agent_status",
    "needs_human_reason",
    "proposal_markup_id",
    "proposal_expires_at",
    "apply",
    "resolved_package_hash"
  ]
};

// src/markups/legacyReviewFields.ts
var LEGACY = legacy_review_fields_default;
var LEGACY_REVIEW_FIELDS = LEGACY.fields;
var FRESH_REVIEW_STATUS = LEGACY.freshStatus;

// src/markups/reviewStatus.ts
var REVIEW_STATUSES = [
  "open",
  "grounded",
  "proposed",
  "accepted",
  "rejected",
  "applying",
  "resolved",
  "restored",
  "needs-human"
];
var DEFAULT_REVIEW_STATUS = FRESH_REVIEW_STATUS;
var REVIEW_TRACE = { field: "trace_id", pattern: "^[A-Za-z0-9_-]{6,64}$" };
var DISPATCH_LOCK = { field: "dispatch_id" };
function isDispatchLocked(m) {
  const v = m?.[DISPATCH_LOCK.field];
  return typeof v === "string" && v !== "";
}
var REVIEW_SELECTION = { maxAlternatives: 3 };
var REVIEW_NOTE_MAX_LENGTH = 280;

// src/markups/review-request-contract.json
var review_request_contract_default = {
  $comment: "Review REQUEST contract v1 (2026-10-06, wiki spec--review-revamp R5; build record R5-FINAL). The reviewer's unit of work is the REQUEST a Dispatch creates (projects/{p}/requests/{trace_id}); the OP chain (projects/{p}/operations/{op_id}, append-only per op) carries the model state; ONE service (apps/pdfmarkuptool/functions/src/review/requests.ts + requestStore.ts) moves both, and nothing else writes either. Ink is the project's markups (projects/{p}/documents/{d}/markups/{id}) plus `request_id`; owned ink (request_id set, its request not `editing`) takes no client write. Every rule the engine applies is a row below: lookup by verb (+ case) -> the rows whose `from` holds the status of `record` (`request` or `op`; `creates` rows need no request) -> without a case, a caseless row wins over cased ones, else case-required -> the first row listing the actor. Invariant keys: inkLock set|clear (ink request_id written in the same transaction; set refuses `owned`), lease take|renew|clear|drop (take mints a new token; renew and clear CAS on the caller's token -> `lease-lost`; drop removes a lease without a token: a reviewer's Release), takeover (a take from a leased status needs the lease expired or held by the same worker), cas (call fields that must equal the record's current value), createsOp (a new op in the same transaction; `of` from the latestOp entry that matched), latestOp (the request's latest op must match one entry), latestOpNot (its status must not be one of these), notReleased, setsReleased, attemptsBelow / attemptsInc / renderAttemptsInc (limits), alsoRequest (D8: the same verdict moves the request too), keepsStatus, noHistory, noCalls (beats keep no idempotency entry), message (the thread entry type). The reviewer's REPLY (2026-10-07) is `request.post [correction]`: from proposed, needs-you, or a failed apply it sends the request back to the drafter (queued -> proposal n+1). RELEASE is the one way to give ink back: before Accept and after a failed apply (`failed-apply`: nothing was applied) it cancels the request and frees the ink; once a change landed (`accepted`) it hides the ink with the request; after a Restore took the change back out (`restored`, 2026-10-09: a restore applied or rendered, or a failed reapply on top of one -- the model no longer holds the change) it cancels the request and frees the ink like `failed-apply`, so the reviewer's ink comes back free to edit and Send again. `request.reopen` stays in the table (records already `editing` still Send again) but no client offers it. Vendored byte-identical to apps/pdfmarkuptool/functions/src/review/ (requestContract.test.ts), pdf-cli internal/reviewcore/ and the drafter's scripts/worker/ (R5-1 part 2 / R5-2). R6 (2026-10-07, additive, requestVersion unchanged; wiki spec--review-revamp R6): the same records on Firestore -- number.counterDoc (the counter off the project doc), limits.docBytes (refusal too-large), refusal contention (a store transaction that kept aborting; retry with the same op_id), reads worker.poll, and clientWrites (what a client may not write on a gated project, for firestore.rules, the local guard and the SPA alike). 2026-10-08 (additive): `progress` + limits.progress -- the live-steps line, until then hand-typed in the engine, the drafter and pdf-cli. 2026-10-09 (requestVersion unchanged, like the 2026-10-07 Release split; only a reviewer calls request.release): the `restored` Release case, split out of `accepted` -- Release after a successful Restore gave the ink back hidden, throwing the reviewer's intent away (prod tr-mec3fpo4kplia4lhaklf).",
  requestVersion: 1,
  collections: { requests: "requests", operations: "operations", markups: "markups" },
  gate: { field: "reviewCore", value: 2 },
  inkField: "request_id",
  number: {
    $comment: "U2 (2026-10-07): the request's per-project NUMBER, the `#4` a reviewer reads on the canvas badge, the rail row and the toasts. The creating move assigns it in its own transaction: 1 + the project doc's `counter` (the highest number assigned so far; absent = 0), and writes the counter back in that transaction, so two concurrent Dispatches get distinct numbers (the second one's transaction retries). Never reused, never changed by a later verb; a released or cancelled request keeps its number. Optional on a record: requests created before this field render without a number. The counter is service-only (firestore.rules, local/src/opsRules.ts). R6: the counter lives in its own doc, requests/{counterDoc} (field `counter`), not on the project doc -- every request transaction reads the project doc for the gate, so a counter there tied each Dispatch to every other request transaction; a project-doc `counter` from before R6 is read once as the floor. counterDoc is not a request: readers skip any id without the trace prefix.",
    field: "number",
    counter: "requestNumber",
    counterDoc: "_counter"
  },
  clientWrites: {
    $comment: "R6: what a CLIENT may not write on a project with the gate -- one list for the three enforcers: firestore.rules (the helpers apps/pdfmarkuptool/scripts/gen-rules-review-request.mjs generates), the local server's guard (local/src/opsRules.ts) and the SPA's gated write path (services/markupFields.ts strips them). inkFields: never written by a client, create or update -- the ink field only the request service writes (inkField) and the v8 review fields the SPA projects onto ink in memory (the R2 dispatch lock + its released flag, trace_id). inkStateFields: the v8 review state the SPA also projects: an update never touches it, a create carries at most the fresh state every create pipeline writes (the v8 default status, an empty history). refusedWhenGated: the v8 client collections a gated project closes (its reviewer actions go through the request service). On EVERY project: inkField, gate.field and number.counter are service-only, and the requests collection is member-read and service-written.",
    inkFields: ["request_id", "dispatch_id", "released", "trace_id"],
    inkStateFields: ["status", "status_history"],
    refusedWhenGated: ["reviewer_events"]
  },
  ids: {
    $comment: "D2: deterministic ids so a retried call lands on the same record. trace_id = trace.prefix + base32(sha256(projectId + ':' + op_id))[0..length]; a created op = op.prefix + base32(sha256(trace_id + ':' + kind + ':' + n + ':' + seq))[0..length] (n = the accepted proposal, seq = the request's op count before it). base32 = RFC 4648, lowercase alphabet below, no padding. op_id is the CALLER's call id (one per tap / per worker step).",
    alphabet: "abcdefghijklmnopqrstuvwxyz234567",
    length: 20,
    trace: { prefix: "tr-" },
    op: { prefix: "op-" },
    callPattern: "^[A-Za-z0-9_-]{6,64}$"
  },
  actors: {
    admin: { byType: "human" },
    editor: { byType: "human" },
    drafter: { byType: "agent" }
  },
  statuses: {
    request: {
      queued: { initial: true },
      working: { leased: true },
      proposed: {},
      "needs-you": {},
      editing: { inkEditable: true },
      accepted: {},
      cancelled: { terminal: true, inkFree: true }
    },
    op: {
      queued: { initial: true },
      applying: { leased: true },
      applied: {},
      rendered: { terminal: true },
      failed: { terminal: true }
    }
  },
  opKinds: ["apply", "restore", "reapply"],
  stages: ["reading", "drafting", "verifying"],
  progress: {
    $comment: "2026-10-08 (the live-steps line, #481): `field` is the drafter's one-line progress on a working request (\"Step 4 . 0:31 -- Searching the content library\"). Only `verb` (request.beat, holder only) writes it: a string of at most limits.progress, kept by a beat that omits it (the lease thread's beats), dropped with `stage` when the request leaves working. Display only (the rail's state line); nothing reads it as state.",
    field: "progress_text",
    verb: "request.beat"
  },
  scopes: ["selection"],
  messageTypes: ["note", "correction", "reason", "proposal", "decision"],
  proposal: {
    $comment: 'D6: alternatives[0] is the primary request; each alternative carries its own footprint + cursor (required for the drivers below) and its own inverse (no restore_plan -> no Restore). 2026-10-17 (additive, requestVersion unchanged; wiki spec--drafter-photo-survey 3e): an alternative may carry `items` [{key, label, approx?, warning?}] (at most maxItems, keys unique, label and warning at most limits.summary) -- DISPLAY DATA only, the card\'s checklist: nothing reads it at Accept and no op carries it; unticking items posts an ordinary request.post [correction] "Leave out: <labels>". 2026-10-19 (additive, requestVersion unchanged; wiki spec--drafter-product-survey "The card, Accept, Restore", A1, slice S8): an item may carry ONE `image` {asset, w, h} -- its evidence picture (photo crop | product page image | our model); `asset` is a promoted photo\'s reference in the form a markup\'s media item uses (64 lowercase hex = sha256 of the JPEG at projects/{p}/assets/{asset}.jpg; itemImage.assetRe = asset-contract.json assetRe), w and h integer pixels 1..itemImage.maxEdge (= asset-contract.json limits.edge); other keys are dropped. DISPLAY DATA only, like `items`: nothing reads it at Accept and no op carries it; a malformed image is refused bad-args like a malformed item.',
    footprintDrivers: ["revit"],
    maxAlternatives: 4,
    maxItems: 40,
    itemImage: { assetRe: "^[0-9a-f]{64}$", maxEdge: 4096 }
  },
  limits: {
    ink: { min: 1, max: 200 },
    leaseSeconds: 600,
    calls: 50,
    attempts: 2,
    renderAttempts: 3,
    text: 2e3,
    summary: 600,
    progress: 600,
    docBytes: 921600
  },
  refusals: ["unknown-verb", "illegal-transition", "forbidden-actor", "case-required", "stale-version", "bad-args", "bad-message", "not-found", "not-enabled", "owned", "lease-held", "lease-lost", "too-large", "contention"],
  moves: [
    { verb: "request.dispatch", record: "request", from: [], to: "queued", actors: ["admin", "editor"], creates: true, inkLock: "set" },
    { verb: "request.claim", record: "request", from: ["queued"], to: "working", actors: ["drafter"], lease: "take" },
    { verb: "request.claim", case: "reclaim", record: "request", from: ["working"], to: "working", actors: ["drafter"], lease: "take", takeover: true },
    { verb: "request.beat", record: "request", from: ["working"], keepsStatus: true, actors: ["drafter"], lease: "renew", noHistory: true, noCalls: true },
    { verb: "request.propose", record: "request", from: ["working"], to: "proposed", actors: ["drafter"], lease: "clear", proposal: "new", message: "proposal" },
    { verb: "request.needs_you", record: "request", from: ["working"], to: "needs-you", actors: ["drafter"], lease: "clear", error: "required", message: "reason" },
    { verb: "request.accept", record: "request", from: ["proposed"], to: "accepted", actors: ["admin"], cas: ["n", "alt"], createsOp: { kind: "apply" }, message: "decision" },
    { verb: "request.reject", record: "request", from: ["proposed"], to: "queued", actors: ["admin", "editor"], cas: ["n"], message: "decision" },
    { verb: "request.reopen", record: "request", from: ["needs-you"], to: "editing", actors: ["admin", "editor"], message: "decision" },
    { verb: "request.reopen", case: "after-op", record: "request", from: ["accepted"], to: "editing", actors: ["admin", "editor"], notReleased: true, latestOp: [{ kind: "apply", status: "failed" }, { kind: "restore", status: "rendered" }], message: "decision" },
    { verb: "request.send", record: "request", from: ["editing"], to: "queued", actors: ["admin", "editor"], inkLock: "set" },
    { verb: "request.release", record: "request", from: ["queued", "working", "proposed", "needs-you", "editing"], to: "cancelled", actors: ["admin", "editor"], inkLock: "clear", lease: "drop", message: "decision" },
    { verb: "request.release", case: "accepted", record: "request", from: ["accepted"], keepsStatus: true, actors: ["admin", "editor"], notReleased: true, latestOp: [{ kind: "apply", status: "applied" }, { kind: "apply", status: "rendered" }, { kind: "restore", status: "failed" }, { kind: "reapply", status: "applied" }, { kind: "reapply", status: "rendered" }], setsReleased: true, message: "decision" },
    { verb: "request.release", case: "restored", record: "request", from: ["accepted"], to: "cancelled", actors: ["admin", "editor"], notReleased: true, latestOp: [{ kind: "restore", status: "applied" }, { kind: "restore", status: "rendered" }, { kind: "reapply", status: "failed" }], inkLock: "clear", message: "decision" },
    { verb: "request.release", case: "failed-apply", record: "request", from: ["accepted"], to: "cancelled", actors: ["admin", "editor"], notReleased: true, latestOp: [{ kind: "apply", status: "failed" }], inkLock: "clear", message: "decision" },
    { verb: "request.post", case: "note", record: "request", from: ["queued", "working", "proposed", "needs-you", "editing", "accepted"], keepsStatus: true, actors: ["admin", "editor", "drafter"], message: "note", requiresText: true, noHistory: true },
    { verb: "request.post", case: "correction", record: "request", from: ["proposed", "needs-you"], to: "queued", actors: ["admin", "editor"], message: "correction", requiresText: true },
    { verb: "request.post", case: "correction", record: "request", from: ["accepted"], to: "queued", actors: ["admin", "editor"], notReleased: true, latestOp: [{ kind: "apply", status: "failed" }], message: "correction", requiresText: true },
    { verb: "request.post", case: "correction", record: "request", from: ["queued", "working", "editing"], keepsStatus: true, actors: ["admin", "editor"], message: "correction", requiresText: true, noHistory: true },
    { verb: "op.restore", record: "request", from: ["accepted"], keepsStatus: true, actors: ["admin"], latestOp: [{ kind: "apply", status: "rendered", of: "self" }, { kind: "reapply", status: "rendered", of: "self" }, { kind: "restore", status: "failed", of: "of_op" }], createsOp: { kind: "restore", restorable: true } },
    { verb: "op.reapply", record: "request", from: ["accepted"], keepsStatus: true, actors: ["admin"], latestOp: [{ kind: "restore", status: "rendered", of: "self" }, { kind: "reapply", status: "failed", of: "of_op" }], createsOp: { kind: "reapply" } },
    { verb: "op.begin", record: "op", from: ["queued"], to: "applying", actors: ["drafter"], lease: "take", attemptsInc: true },
    { verb: "op.reclaim", record: "op", from: ["applying"], keepsStatus: true, actors: ["drafter"], lease: "take", takeover: true },
    { verb: "op.beat", record: "op", from: ["applying"], keepsStatus: true, actors: ["drafter"], lease: "renew", noHistory: true, noCalls: true },
    { verb: "op.end", case: "applied", record: "op", from: ["applying"], to: "applied", actors: ["drafter"], lease: "clear" },
    { verb: "op.end", case: "failed", record: "op", from: ["applying"], to: "failed", actors: ["drafter"], lease: "clear", error: "required" },
    { verb: "op.end", case: "conflict", record: "op", from: ["applying"], to: "failed", actors: ["drafter"], lease: "clear", error: "required", kinds: ["apply"], alsoRequest: { from: ["accepted"], to: "working", lease: "take", stage: "verifying" } },
    { verb: "op.retry", record: "op", from: ["applying"], to: "queued", actors: ["drafter"], lease: "clear", attemptsBelow: "attempts" },
    { verb: "op.render", record: "op", from: ["applied"], to: "rendered", actors: ["drafter"], requires: ["revision_after"] },
    { verb: "op.render", case: "failed", record: "op", from: ["applied"], keepsStatus: true, actors: ["drafter"], error: "required", renderAttemptsInc: true }
  ],
  reads: {
    $comment: "Read verbs (no transaction writes): any member of the project (owner or an active permission of any role) or the drafter.",
    "requests.list": { filters: ["status"] },
    "request.get": { requires: ["trace_id"] },
    "ops.list": { filters: ["request_id", "status"], requiresOne: ["request_id", "status"] },
    "worker.poll": { $comment: "R6: the worker's one read per tick -> {working, queued, ops: {queued, applied}} (requests by status; request-contract ops only)." }
  },
  cardStates: {
    $comment: "D9: the card a request shows = its status, and once accepted its latest op (ops sorted by seq). First matching row wins. `stage` overrides the request's stage on the card; `error` = show the op's error.user_text; `renderFailed` applies to a latest op in its `status` whose render_attempts reached limits.renderAttempts. `released` hides the card (history only).",
    noRequest: "not-sent",
    released: "hidden",
    byStatus: {
      queued: "drafter",
      working: "drafter",
      proposed: "proposed",
      "needs-you": "needs-you",
      editing: "editing",
      cancelled: "not-sent"
    },
    renderFailed: { status: "applied", card: "needs-you", user_text: "Applied, but the sheet did not refresh" },
    accepted: [
      { status: ["queued", "applying", "applied"], card: "drafter", stage: "applying" },
      { status: ["rendered"], kind: ["apply", "reapply"], card: "done" },
      { status: ["rendered"], kind: ["restore"], card: "restored" },
      { status: ["failed"], kind: ["apply"], card: "needs-you", error: true },
      { status: ["failed"], kind: ["restore"], card: "done", error: true },
      { status: ["failed"], kind: ["reapply"], card: "restored", error: true }
    ],
    acceptedNoOp: { card: "drafter", stage: "applying" }
  }
};

// src/markups/sha256.ts
var K = new Uint32Array([
  1116352408,
  1899447441,
  3049323471,
  3921009573,
  961987163,
  1508970993,
  2453635748,
  2870763221,
  3624381080,
  310598401,
  607225278,
  1426881987,
  1925078388,
  2162078206,
  2614888103,
  3248222580,
  3835390401,
  4022224774,
  264347078,
  604807628,
  770255983,
  1249150122,
  1555081692,
  1996064986,
  2554220882,
  2821834349,
  2952996808,
  3210313671,
  3336571891,
  3584528711,
  113926993,
  338241895,
  666307205,
  773529912,
  1294757372,
  1396182291,
  1695183700,
  1986661051,
  2177026350,
  2456956037,
  2730485921,
  2820302411,
  3259730800,
  3345764771,
  3516065817,
  3600352804,
  4094571909,
  275423344,
  430227734,
  506948616,
  659060556,
  883997877,
  958139571,
  1322822218,
  1537002063,
  1747873779,
  1955562222,
  2024104815,
  2227730452,
  2361852424,
  2428436474,
  2756734187,
  3204031479,
  3329325298
]);
function sha256Hex(text) {
  const msg = new TextEncoder().encode(text);
  const bitLen = msg.length * 8;
  const padded = new Uint8Array(msg.length + 9 + 63 >> 6 << 6);
  padded.set(msg);
  padded[msg.length] = 128;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLen / 4294967296));
  view.setUint32(padded.length - 4, bitLen >>> 0);
  const h = new Uint32Array([1779033703, 3144134277, 1013904242, 2773480762, 1359893119, 2600822924, 528734635, 1541459225]);
  const w = new Uint32Array(64);
  const rotr = (x, n) => x >>> n | x << 32 - n;
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const a2 = w[i - 15];
      const b2 = w[i - 2];
      const s0 = rotr(a2, 7) ^ rotr(a2, 18) ^ a2 >>> 3;
      const s1 = rotr(b2, 17) ^ rotr(b2, 19) ^ b2 >>> 10;
      w[i] = w[i - 16] + s0 + w[i - 7] + s1 >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7]];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = e & f ^ ~e & g;
      const t1 = hh + S1 + ch + K[i] + w[i] >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = a & b ^ a & c ^ b & c;
      const t2 = S0 + maj >>> 0;
      hh = g;
      g = f;
      f = e;
      e = d + t1 >>> 0;
      d = c;
      c = b;
      b = a;
      a = t1 + t2 >>> 0;
    }
    h[0] = h[0] + a >>> 0;
    h[1] = h[1] + b >>> 0;
    h[2] = h[2] + c >>> 0;
    h[3] = h[3] + d >>> 0;
    h[4] = h[4] + e >>> 0;
    h[5] = h[5] + f >>> 0;
    h[6] = h[6] + g >>> 0;
    h[7] = h[7] + hh >>> 0;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, "0")).join("");
}

// src/markups/reviewRequest.ts
var REQUEST_CONTRACT = review_request_contract_default;
var REQUEST_STATUSES = Object.keys(REQUEST_CONTRACT.statuses.request);
var OP_STATUSES = Object.keys(REQUEST_CONTRACT.statuses.op);
var REQUEST_VERBS = [...new Set(REQUEST_CONTRACT.moves.map((m) => m.verb))];
var REQUEST_INITIAL_STATUS = REQUEST_STATUSES.find((s) => REQUEST_CONTRACT.statuses.request[s].initial);
var OP_INITIAL_STATUS = OP_STATUSES.find((s) => REQUEST_CONTRACT.statuses.op[s].initial);
var REQUEST_EDITING_STATUS = REQUEST_STATUSES.find((s) => REQUEST_CONTRACT.statuses.request[s].inkEditable);
var REQUEST_INK_WRITABLE_STATUSES = REQUEST_STATUSES.filter(
  (s) => REQUEST_CONTRACT.statuses.request[s].inkEditable || REQUEST_CONTRACT.statuses.request[s].inkFree
);
var requestOwnsInk = (status) => !status || !REQUEST_INK_WRITABLE_STATUSES.includes(status);
function findRequestMove(verb, kase, status, actor) {
  const rows = REQUEST_CONTRACT.moves.map((m2, index2) => ({ m: m2, index: index2 })).filter(({ m: m2 }) => m2.verb === verb && (kase === void 0 || m2.case === kase));
  if (!rows.length) return { ok: false, code: "unknown-verb" };
  let here = rows.filter(({ m: m2 }) => status === null ? !!m2.creates : !m2.creates && m2.from.includes(status));
  if (!here.length) return { ok: false, code: "illegal-transition" };
  if (kase === void 0) {
    const cases = [...new Set(here.map(({ m: m2 }) => m2.case ?? ""))];
    if (cases.length > 1) {
      if (!cases.includes("")) return { ok: false, code: "case-required", cases };
      here = here.filter(({ m: m2 }) => m2.case === void 0);
    }
  }
  const mine = here.filter(({ m: m2 }) => m2.actors.includes(actor));
  if (!mine.length) return { ok: false, code: "forbidden-actor" };
  const { m, index } = mine[0];
  return { ok: true, move: m, index, to: m.keepsStatus ? status : m.to ?? null };
}
function base32OfHex(hex) {
  const A = REQUEST_CONTRACT.ids.alphabet;
  let out = "";
  let bits = 0;
  let acc = 0;
  for (let i = 0; i + 1 < hex.length; i += 2) {
    acc = acc << 8 | parseInt(hex.slice(i, i + 2), 16);
    bits += 8;
    while (bits >= 5) {
      out += A[acc >>> bits - 5 & 31];
      bits -= 5;
    }
    acc &= (1 << bits) - 1;
  }
  if (bits > 0) out += A[acc << 5 - bits & 31];
  return out;
}
var idOf = (prefix, text) => prefix + base32OfHex(sha256Hex(text)).slice(0, REQUEST_CONTRACT.ids.length);
function requestTraceId(projectId, opId) {
  return idOf(REQUEST_CONTRACT.ids.trace.prefix, `${projectId}:${opId}`);
}
function requestOpId(traceId, kind, n, seq) {
  return idOf(REQUEST_CONTRACT.ids.op.prefix, `${traceId}:${kind}:${n}:${seq}`);
}
function latestOpOf(ops) {
  let best = null;
  for (const o of ops) if (!best || (o.seq ?? 0) >= (best.seq ?? 0)) best = o;
  return best;
}
function cardStateOfRequest(request, ops = []) {
  const T = REQUEST_CONTRACT.cardStates;
  if (!request) return { card: T.noRequest };
  if (request.released) return { card: T.released };
  const byStatus = T.byStatus[request.status];
  if (byStatus !== void 0) {
    const out2 = { card: byStatus };
    if (request.stage) out2.stage = request.stage;
    if (request.error?.user_text) out2.error = request.error.user_text;
    return out2;
  }
  const op = latestOpOf(ops);
  if (!op) return { ...T.acceptedNoOp };
  if (op.status === T.renderFailed.status && (op.render_attempts ?? 0) >= REQUEST_CONTRACT.limits.renderAttempts) {
    return { card: T.renderFailed.card, error: T.renderFailed.user_text };
  }
  const row = T.accepted.find((r) => r.status.includes(op.status) && (!r.kind || r.kind.includes(op.kind)));
  if (!row) return { ...T.acceptedNoOp };
  const out = { card: row.card };
  if (row.stage) out.stage = row.stage;
  if (row.error && op.error?.user_text) out.error = op.error.user_text;
  return out;
}

// src/markups/review-request-contract.v2.json
var review_request_contract_v2_default = {
  $comment: "Review REQUEST contract v2 (2026-10-24, wiki spec--review-foundation v4, slice S1). Replaces v1's 30 move rows (review-request-contract.json) with three records (Request, Task, Entry), one task lifecycle, a kinds registry and eight verbs. D6: one cutover -- v2 lives beside v1 only on the slice branches until S5; there are no aliases, no backfill and no dual engine. The ONE engine is reviewRequestV2.ts (pure: decide / allowedActions / phaseOf / netEffectOf / cardOf); every rule it applies is data below. Lookup of a call: the verb (unknown -> unknown-verb) -> the record it moves (no request -> not-found; dispatch needs none) -> the phase / task state the verb reads (`from`, `phases`, `rules`; none -> illegal-transition) -> the actor (`actors`, or the kind's `claim` for task verbs; none -> forbidden-actor) -> the guards (lease, cas, latest-only, restorable, text) -> the writes. `phase` is MATERIALIZED: the engine writes phaseOf(request, tasks) in the same transaction; no client writes it. Net effect (in | out | unknown) and settled are derived, never stored. Fields marked `reserved` name what later slices own (S10 preview render target, S15 agents + candidates, S16 sources); the engine refuses them `not-enabled`. Kept from v1 byte-for-byte in meaning: collections, gate, inkField, number, clientWrites, ids (the op-id hash keeps the kind label: apply | restore | reapply, plus render), actors, stages, progress, scopes, proposal, limits. Store concerns stay with the store (S2-S4): the idempotency `calls` replay, the per-project number counter transaction; the reads every store serves (requests.list by phase, request.get, tasks.list, worker.poll) are named in `reads` so the service and pdf-cli derive them from one table.",
  requestVersion: 2,
  collections: { requests: "requests", operations: "operations", markups: "markups" },
  gate: { field: "reviewCore", value: 2 },
  inkField: "request_id",
  number: { field: "number", counter: "requestNumber", counterDoc: "_counter" },
  clientWrites: {
    $comment: "Unchanged from v1 (R6): what a client may not write on a gated project. `phase` and `waiting` join the request's service-only fields (the whole requests collection is service-written).",
    inkFields: ["request_id", "dispatch_id", "released", "trace_id"],
    inkStateFields: ["status", "status_history"],
    refusedWhenGated: ["reviewer_events"]
  },
  ids: {
    $comment: "trace_id = trace.prefix + base32(sha256(projectId + ':' + call id))[0..length] (v1 formula). An operations task id = op.prefix + base32(sha256(trace_id + ':' + label + ':' + n + ':' + seq))[0..length]: label = the apply label (apply | restore | reapply) or `render`; n = the accepted proposal; seq = the request's apply-task count before it (render: its apply's seq). The label stays in the hash (spec 'Apply payload and undo'), so v2 ids equal v1 ids for the same chain position and never collide across kinds. A draft task id = draft.prefix + hash(trace_id + ':draft:' + k); a waiting id = waiting.prefix + hash(trace_id + ':waiting:' + k) (k = the request's counter of that thing: a fresh id per opening, never reused).",
    alphabet: "abcdefghijklmnopqrstuvwxyz234567",
    length: 20,
    trace: { prefix: "tr-" },
    op: { prefix: "op-" },
    draft: { prefix: "dr-" },
    waiting: { prefix: "wt-" },
    callPattern: "^[A-Za-z0-9_-]{6,64}$",
    workerPattern: "^[A-Za-z0-9_.@:-]{1,128}$"
  },
  actors: {
    admin: { byType: "human" },
    editor: { byType: "human" },
    drafter: { byType: "agent" }
  },
  $records: "Three records. No generic per-record etag, no `expires`.",
  records: {
    request: {
      home: "requests/{trace_id}",
      fields: ["trace_id", "requestVersion", "number", "ink", "documentId", "page", "scope", "phase", "stage", "progress_text", "waiting", "released", "cancelled", "proposals", "thread", "drafts", "counters", "created_by", "createdAt", "updated_at", "reading"],
      $fields: "ink: the locked markup ids (D12 lock). proposals: [{n, summary, alternatives}] (n = index + 1). drafts: the request's draft tasks (Task records; draft tasks live on the request doc). counters: {drafts, waits} for the fresh ids. released: {by, at} -- hidden, ink kept. cancelled: {by, at} -- terminal, ink free. updated_at: ctx.now of the last call that changed it (the engine writes it on every write; clients order and label by it, never write it). reading: what the drafter read the ink as (see `reading`)."
    },
    task: {
      home: "kinds[kind].home",
      fields: ["id", "kind", "of", "base", "payload", "fingerprint_before", "lease", "attempts", "state", "outcome", "error", "result", "seq", "updated_at", "ended_at"],
      $fields: `of: ONLY the undo target (a restore names the forward it undoes, a reapply the restore); a retry is the same task. lease: {holder, token, until}. attempts: incremented on every take from ready (never on a takeover). base: reserved (S16 sources: source -> {state hash, source_version}). updated_at: ctx.now of the call that last moved its state (create, claim / takeover, retry, complete -- a beat only renews the lease, so a leased task's updated_at is its claim time); ended_at: ctx.now of the call that took it to done | failed (set once). Both engine-written ("Applied 14:20", queue order).`,
      reservedFields: { base: "S16" }
    },
    entry: {
      home: "request.thread[]",
      fields: ["type", "by", "at", "text", "alt", "n", "reading_n"]
    }
  },
  reading: {
    $comment: "D14 (owner, 2026-10-24: \"Add reading to v2\"; v1 parity): the drafter's live reading of the ink, shown to admin / editor (the drafter's-turn \"Read as\" box). Written by the drafter's task.beat on its LEASED draft (holder token; {n >= 1, text, confidence?, alternatives?}). A correction on a request in `replacedBy.phases` that has a reading replaces it with {n: n + 1, text: the correction's text}; the correction may name the reading it fixes (`reading_n`, kept on the entry). The draft side of the correction is unchanged (an open wait -> a new draft; an open draft -> appended).",
    field: "reading",
    fields: ["n", "text", "confidence", "alternatives"],
    writtenBy: { verb: "task.beat", kind: "draft" },
    replacedBy: { entry: "correction", phases: ["working", "proposed"] }
  },
  $phases: "Today's statuses minus `editing`. phaseOf: the first rule of phaseRules that holds.",
  phases: {
    queued: { initial: true },
    working: {},
    proposed: {},
    "needs-you": {},
    accepted: {},
    cancelled: { terminal: true, inkFree: true }
  },
  phaseRules: [
    { if: "cancelled", phase: "cancelled" },
    { if: "waiting:proposal", phase: "proposed" },
    { if: "waiting:question", phase: "needs-you" },
    { if: "draft:leased", phase: "working" },
    { if: "draft:ready", phase: "queued" },
    { if: "apply", phase: "accepted" }
  ],
  resting: {
    $comment: "A request with nothing left to do: the stuck-state check needs no exit from it. cancelled (terminal) or released (hidden) with no open task.",
    phases: ["cancelled"],
    releasedWithNoOpenTask: true
  },
  waiting: {
    $comment: "D7: the one open human wait, at most one, a fresh id per opening; respond(id, accept i) CASes on waiting.id. Notes, beats, progress_text and preview attachments never touch it. A question opens only for: a drafter question, a failed or unknown FIRST forward apply (see kinds.apply.outcomes[*].question).",
    field: "waiting",
    kinds: ["proposal", "question"]
  },
  taskStates: {
    ready: { initial: true },
    leased: { leased: true },
    done: { terminal: true },
    failed: { terminal: true }
  },
  lease: {
    $comment: "ONE lease helper for every kind and both task homes. take: ready -> leased, a NEW token (a random UUID the caller mints: ctx.token), attempts + 1. takeover: a take of a leased task -- needs the lease expired or held by the same worker, keeps attempts, and returns the kind's recover hook name when it has one. renew (beat) and clear (complete) CAS on the caller's token; a stale holder gets lease-lost; a live lease of another worker is lease-held.",
    seconds: 600,
    token: "uuid",
    lost: "lease-lost",
    held: "lease-held"
  },
  directions: ["forward", "inverse"],
  labels: {
    $comment: "D8: one apply task, `direction` + `of` name the label (the op-id hash and the card read it). Inverse = restore; forward with `of` = reapply.",
    apply: { direction: "forward", of: false },
    reapply: { direction: "forward", of: true },
    restore: { direction: "inverse", of: true }
  },
  $kinds: "Layer 4: four fields per kind -- claim (who may claim), attempts (retry policy; null = unlimited takeovers, no retry), outcomes (-> follow-up or waiting), recover (the hook a takeover of a side-effecting task calls). A new kind is a row, zero verbs. outcome fields: state (the task's end state), retry (back to ready while attempts < the kind's attempts, else `exhausted`), error (required: {kind, user_text}), opens (waiting kind), question (`first`: opens a question only on the first forward apply), only (`first`: refused on any other label), redraft (the request back to a draft leased to the same worker, same transaction), followUp (a new task), requires (call fields).",
  kinds: {
    draft: {
      home: "request",
      claim: ["drafter"],
      attempts: null,
      recover: null,
      outcomes: {
        proposal: { state: "done", opens: "proposal", requires: ["proposal"] },
        question: { state: "done", opens: "question", error: "required", entry: "reason" }
      }
    },
    verify: {
      reserved: "S15: agent-hosted candidates -- post(candidate) creates a verify task the host claims. Not enabled in S1.",
      home: "operations",
      claim: ["drafter"],
      attempts: null,
      recover: null,
      outcomes: {}
    },
    apply: {
      home: "operations",
      claim: ["drafter"],
      attempts: 2,
      recover: "recoverApply",
      sideEffects: true,
      outcomes: {
        applied: { state: "done", followUp: { kind: "render", target: "op" } },
        failed: { state: "failed", error: "required", question: "first" },
        interrupted: { state: "failed", error: "optional", question: "first", defaultError: { kind: "apply-interrupted", user_text: "May or may not have applied -- check the model" } },
        stale: { state: "failed", error: "required", only: "first", redraft: { stage: "verifying" } },
        "not-applied": { retry: true, exhausted: "interrupted" }
      }
    },
    render: {
      home: "operations",
      claim: ["drafter"],
      attempts: 3,
      recover: null,
      targets: {
        op: {},
        preview: { reserved: "S10: {alt i under rollback}; payload {n, alt, alt cursor}; attach CAS on n; cancelled when waiting closes (D9)." }
      },
      outcomes: {
        rendered: { state: "done", requires: ["revision_after"] },
        failed: { retry: true, exhausted: "failed", error: "required" }
      }
    }
  },
  net: {
    $comment: "Net effect over the LATEST apply task (by seq): first matching row. An open latest apply = in flight (no effect yet). settled = the latest apply failed, or its render task is done or failed (attempts exhausted).",
    rows: [
      { state: "done", direction: "forward", effect: "in" },
      { state: "done", direction: "inverse", effect: "out" },
      { state: "failed", outcome: ["interrupted"], effect: "unknown" },
      { state: "failed", direction: "inverse", effect: "in" },
      { state: "failed", direction: "forward", effect: "out" }
    ]
  },
  verbs: {
    dispatch: { actors: ["admin", "editor"], creates: "request", inkLock: "set", task: "draft" },
    "task.claim": { from: ["ready"], lease: "take", takeover: { from: ["leased"] } },
    "task.beat": { from: ["leased"], lease: "renew" },
    "task.complete": { from: ["leased"], lease: "clear" },
    "task.create": {
      actors: ["admin"],
      phases: ["accepted"],
      kinds: ["apply"],
      $comment: "Policy latest-only (D8; selective undo modelled, not enabled): the request's latest apply must match one `latest` entry. `of`: self = that task, of = the task it names. restorable: the forward's alternative carries a restore_plan.",
      rules: [
        { direction: "inverse", restorable: true, latest: [
          { label: "apply", state: "done", rendered: true, of: "self" },
          { label: "reapply", state: "done", rendered: true, of: "self" },
          { label: "restore", state: "failed", of: "of" }
        ] },
        { direction: "forward", latest: [
          { label: "restore", state: "done", rendered: true, of: "self" },
          { label: "reapply", state: "failed", of: "of" }
        ] }
      ]
    },
    respond: { $comment: "D16 (owner \"Go\", 2026-10-24): `copies` = the accepted alternative's fields copied onto the apply task payload (absent ones are left out); task.create clones its source task's payload, so a restore / reapply carries the same preimage_ref -- a task is self-contained.", actors: ["admin"], waiting: "proposal", cas: ["waiting_id", "n"], creates: { kind: "apply", direction: "forward" }, copies: ["request", "footprint", "cursor", "restore_plan", "preimage_ref"], entry: "decision" },
    post: { entries: "entries" },
    cancel: {
      actors: ["admin", "editor"],
      notReleased: true,
      entry: "decision",
      $comment: "First matching rule. in -> released (hidden, ink kept); out or unknown -> cancelled, ink free. No rule (an apply in flight) -> illegal-transition. The `inverse` unknown row keeps v1 (Release after an interrupted Restore hid the request): see docs/review-v2-diff.md Questions.",
      rules: [
        { phases: ["queued", "working", "proposed", "needs-you"], to: "cancelled" },
        { phases: ["accepted"], effect: "in", to: "released" },
        { phases: ["accepted"], effect: "out", to: "cancelled" },
        { phases: ["accepted"], effect: "unknown", direction: "inverse", to: "released" },
        { phases: ["accepted"], effect: "unknown", to: "cancelled" }
      ]
    }
  },
  reads: {
    $comment: "The reads every store serves beside the verbs (S3a service, local server; pdf-cli derives its read verbs and flags from this block). args: name = the call field; field = the stored field it filters on (equality; absent = not a filter, e.g. a doc id); values = the table key whose keys are the legal values (`trace`: a trace id, ids.trace); required = the call needs it; atLeastOne = at least one arg is given. Requests come back without the store's idempotency list; tasks are the operations tasks (draft tasks live on the request doc), with the store's request index field `taskRequestField`, ordered by seq. request.get answers the caller's card (cardOf) and actions (allowedActions, Layer 3; none for a viewer). worker.poll is a drafter's one read per tick: each query is one indexed equality read.",
    taskRequestField: "request_id",
    "requests.list": { record: "request", args: [{ name: "phase", field: "phase", values: "phases" }], returns: ["requests"] },
    "request.get": { record: "request", args: [{ name: "trace_id", values: "trace", required: true }], returns: ["request", "tasks", "card", "actions"] },
    "tasks.list": { record: "task", args: [{ name: "request_id", field: "request_id", values: "trace" }, { name: "state", field: "state", values: "taskStates" }], atLeastOne: true, order: "seq", returns: ["tasks"] },
    "worker.poll": {
      args: [],
      queries: {
        queued: { record: "request", phase: "queued" },
        working: { record: "request", phase: "working" },
        "tasks.ready": { record: "task", state: "ready" },
        "tasks.leased": { record: "task", state: "leased" }
      },
      returns: ["queued", "working", "tasks"]
    }
  },
  $entries: "Thread items. post(entry) takes the ones with `actors`; the others are written by a verb. correction: an open waiting is resolved by the engine and a draft queued; an open draft gets it appended; else refused (no correction after a restore).",
  entries: {
    note: { actors: ["admin", "editor", "drafter"], phases: ["queued", "working", "proposed", "needs-you", "accepted"], requiresText: true },
    correction: { actors: ["admin", "editor"], phases: ["queued", "working", "proposed", "needs-you"], requiresText: true, notReleased: true, resolvesWaiting: true, appendsToDraft: true },
    reason: { writtenBy: "task.complete(draft, question)" },
    decision: { writtenBy: "respond | cancel" },
    candidate: { reserved: "S15: agent-hosted candidates (post(candidate) -> a verify task)." }
  },
  reserved: {
    $comment: "Named here so no slice invents them; the engine refuses them not-enabled.",
    preview: "S10: render target {alt i under rollback}; preview {status ok|none, reason?, pages[{sheet, asset, sidecar_hash}]}; time box; preempt.",
    agents: "S15: permissions records of kind agent (principal + role); the describe digest on task.claim; hard / soft pins.",
    sources: "S16-S17: Task.base = source -> {state hash, source_version}; session claims; multi-source compensation; central sync."
  },
  stages: ["reading", "drafting", "verifying"],
  progress: { field: "progress_text", verb: "task.beat" },
  scopes: ["selection"],
  proposal: {
    $comment: "Unchanged from v1: alternatives[0] is the primary request; each alternative carries its own request, footprint + cursor and its own restore_plan (none -> no Restore). Display-only items / item images as v1.",
    footprintDrivers: ["revit"],
    maxAlternatives: 4,
    maxItems: 40,
    itemImage: { assetRe: "^[0-9a-f]{64}$", maxEdge: 4096 }
  },
  limits: {
    ink: { min: 1, max: 200 },
    text: 2e3,
    summary: 600,
    progress: 600,
    docBytes: 921600
  },
  refusals: ["unknown-verb", "illegal-transition", "forbidden-actor", "stale-version", "bad-args", "bad-message", "not-found", "not-enabled", "owned", "lease-held", "lease-lost", "too-large", "contention"],
  cards: {
    $comment: "The card a request shows (S3 renders it): from waiting + net effect. First matching row of `accepted` wins; `renderFailed` = the latest apply done and its render task failed.",
    noRequest: "not-sent",
    released: "hidden",
    cancelled: "not-sent",
    waiting: { proposal: "proposed", question: "needs-you" },
    draft: "drafter",
    renderFailed: { card: "needs-you", user_text: "Applied, but the sheet did not refresh" },
    accepted: [
      { state: ["ready", "leased"], card: "drafter", stage: "applying" },
      { state: ["done"], render: "open", card: "drafter", stage: "applying" },
      { state: ["done"], direction: "forward", card: "done" },
      { state: ["done"], direction: "inverse", card: "restored" },
      { state: ["failed"], label: ["apply"], card: "needs-you", error: true },
      { state: ["failed"], label: ["restore"], card: "done", error: true },
      { state: ["failed"], label: ["reapply"], card: "restored", error: true }
    ]
  }
};

// src/markups/reviewRequestV2.ts
var REQUEST_CONTRACT_V2 = review_request_contract_v2_default;
function base32OfHexV2(alphabet, hex) {
  let out = "";
  let bits = 0;
  let acc = 0;
  for (let i = 0; i + 1 < hex.length; i += 2) {
    acc = acc << 8 | parseInt(hex.slice(i, i + 2), 16);
    bits += 8;
    while (bits >= 5) {
      out += alphabet[acc >>> bits - 5 & 31];
      bits -= 5;
    }
    acc &= (1 << bits) - 1;
  }
  if (bits > 0) out += alphabet[acc << 5 - bits & 31];
  return out;
}
var idOf2 = (prefix, text) => prefix + base32OfHexV2(REQUEST_CONTRACT_V2.ids.alphabet, sha256Hex(text)).slice(0, REQUEST_CONTRACT_V2.ids.length);
var traceIdV2 = (projectId, callId) => idOf2(REQUEST_CONTRACT_V2.ids.trace.prefix, `${projectId}:${callId}`);
var opIdV2 = (traceId, label, n, seq) => idOf2(REQUEST_CONTRACT_V2.ids.op.prefix, `${traceId}:${label}:${n}:${seq}`);
var draftIdV2 = (traceId, k) => idOf2(REQUEST_CONTRACT_V2.ids.draft.prefix, `${traceId}:draft:${k}`);
var waitingIdV2 = (traceId, k) => idOf2(REQUEST_CONTRACT_V2.ids.waiting.prefix, `${traceId}:waiting:${k}`);
function recover(hook, input, contract = REQUEST_CONTRACT_V2) {
  const kind = Object.values(contract.kinds).find((k) => k.recover === hook);
  if (!kind) throw new Error(`no kind has the recover hook ${hook}`);
  const cap = kind.attempts ?? Infinity;
  const { verdict, cursorNow = null, fingerprintBefore = null, attempts = 0 } = input;
  if (verdict === "holds") return { outcome: "applied", why: "the postcondition holds: the change is in the model" };
  const same = cursorNow != null && fingerprintBefore != null && String(cursorNow) === String(fingerprintBefore);
  if ((verdict === "absent" || verdict === "unknown") && same) {
    if (attempts < cap) return { outcome: "not-applied", why: "not applied and the footprint is unchanged: run it again" };
    return { outcome: "interrupted", why: `not applied, but it already ran ${attempts} times (cap ${cap})` };
  }
  if (verdict === "absent") return { outcome: "interrupted", why: cursorNow == null || fingerprintBefore == null ? "not applied, and the footprint cannot be compared" : "not applied, but the footprint moved since the attempt began" };
  return { outcome: "interrupted", why: "the postcondition cannot be checked" };
}
var Refused = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
  code;
};
var fail = (code, message) => {
  throw new Refused(code, message);
};
var isObj = (v) => !!v && typeof v === "object" && !Array.isArray(v);
var clone = (v) => v === void 0 ? v : JSON.parse(JSON.stringify(v));
var isOpen = (t) => t.state === "ready" || t.state === "leased";
function makeEngine(C) {
  const VERBS = Object.keys(C.verbs);
  const INK_RE = /^[^/\s]{1,128}$/;
  const WORKER_RE = new RegExp(C.ids.workerPattern);
  const TRACE_RE = new RegExp(`^${C.ids.trace.prefix}[${C.ids.alphabet}]{${C.ids.length}}$`);
  const leaseMs = C.lease.seconds * 1e3;
  const untilOf = (now) => new Date(Date.parse(now) + leaseMs).toISOString();
  const expired = (l, now) => Date.parse(l.until) <= Date.parse(now);
  const labelOf = (t) => {
    if (t.kind !== "apply") return t.kind;
    const dir = t.payload.direction;
    const hasOf = t.of !== void 0;
    const hit = Object.keys(C.labels).find((l) => C.labels[l].direction === dir && C.labels[l].of === hasOf);
    return hit ?? "apply";
  };
  const applies = (tasks) => tasks.filter((t) => t.kind === "apply").sort((a, b) => a.seq - b.seq);
  const lastOf = (xs) => xs[xs.length - 1];
  const latestApply = (tasks) => lastOf(applies(tasks)) ?? null;
  const renderOf = (tasks, apply) => lastOf(tasks.filter((t) => t.kind === "render" && t.payload.op === apply.id).sort((a, b) => a.seq - b.seq)) ?? null;
  const openDraft = (r) => r.drafts.find(isOpen) ?? null;
  function phaseOf2(r, tasks) {
    for (const rule of C.phaseRules) {
      const [what, arg] = rule.if.split(":");
      const d = openDraft(r);
      const holds = what === "cancelled" ? !!r.cancelled : what === "waiting" ? r.waiting?.kind === arg : what === "draft" ? d?.state === arg : what === "apply" ? applies(tasks).length > 0 : false;
      if (holds) return rule.phase;
    }
    return Object.keys(C.phases).find((p) => C.phases[p].initial);
  }
  function netEffectOf2(r, tasks) {
    const latest = latestApply(tasks);
    if (!r || !latest) return { effect: null, settled: false, inFlight: false, latest: null, render: null };
    if (isOpen(latest)) return { effect: null, settled: false, inFlight: true, latest, render: null };
    const dir = latest.payload.direction;
    const row = C.net.rows.find((x) => x.state === latest.state && (!x.direction || x.direction === dir) && (!x.outcome || x.outcome.includes(latest.outcome ?? "")));
    const render = latest.state === "done" ? renderOf(tasks, latest) : null;
    const settled = latest.state === "failed" || !!render && !isOpen(render);
    return { effect: row?.effect ?? null, settled, inFlight: false, latest, render };
  }
  function isResting(r, tasks) {
    if (!r) return false;
    if (C.resting.phases.includes(r.phase)) return true;
    return C.resting.releasedWithNoOpenTask && !!r.released && !tasks.some(isOpen) && !r.drafts.some(isOpen);
  }
  function cardOf2(r, tasks) {
    const T = C.cards;
    if (!r) return { card: T.noRequest };
    if (r.released) return { card: T.released };
    if (r.cancelled) return { card: T.cancelled };
    if (r.waiting) {
      const out2 = { card: T.waiting[r.waiting.kind] };
      const e = r.waiting.payload.error;
      if (isObj(e) && typeof e.user_text === "string" && e.user_text) out2.error = e.user_text;
      return out2;
    }
    if (openDraft(r)) return { card: T.draft, ...r.stage ? { stage: r.stage } : {} };
    const ne = netEffectOf2(r, tasks);
    const latest = ne.latest;
    if (!latest) return { card: T.draft };
    if (latest.state === "done" && ne.render?.state === "failed") return { card: T.renderFailed.card, error: T.renderFailed.user_text };
    const label = labelOf(latest);
    const dir = latest.payload.direction;
    const row = T.accepted.find(
      (x) => x.state.includes(latest.state) && (!x.render || (ne.render ? isOpen(ne.render) : true)) && (!x.direction || x.direction === dir) && (!x.label || x.label.includes(label))
    );
    if (!row) return { card: T.draft };
    const out = { card: row.card };
    if (row.stage) out.stage = row.stage;
    if (row.error && latest.error?.user_text) out.error = latest.error.user_text;
    return out;
  }
  const byOf = (a) => ({ id: a.id, role: a.role });
  const textOf = (v, required, what) => {
    if (v === void 0 || v === null || v === "") {
      if (required) fail("bad-args", `${what} needs text`);
      return void 0;
    }
    if (typeof v !== "string") return fail("bad-args", "text must be a string");
    const t = v.trim();
    if (required && !t) fail("bad-args", `${what} needs text`);
    if (t.length > C.limits.text) fail("bad-args", `text is longer than ${C.limits.text}`);
    return t;
  };
  const errorOf = (v, required) => {
    if (v === void 0 || v === null) {
      if (required) fail("bad-args", "error {kind, user_text} is required");
      return void 0;
    }
    if (!isObj(v) || typeof v.kind !== "string" || !v.kind || typeof v.user_text !== "string" || !v.user_text.trim()) return fail("bad-args", "error must be {kind, user_text}");
    const e = { kind: v.kind.slice(0, 80), user_text: v.user_text.trim().slice(0, C.limits.summary) };
    if (typeof v.detail_ref === "string") e.detail_ref = v.detail_ref.slice(0, 512);
    return e;
  };
  const readingOf = (v) => {
    if (!isObj(v) || typeof v.n !== "number" || !Number.isInteger(v.n) || v.n < 1 || typeof v.text !== "string" || v.text.length > C.limits.text) return fail("bad-args", "reading must be {n >= 1, text, confidence?, alternatives?}");
    const out = { n: v.n, text: v.text };
    if (v.confidence !== void 0) {
      if (typeof v.confidence !== "number") fail("bad-args", "reading.confidence must be a number");
      out.confidence = v.confidence;
    }
    if (v.alternatives !== void 0) {
      if (!Array.isArray(v.alternatives) || v.alternatives.length > C.proposal.maxAlternatives) fail("bad-args", "reading.alternatives must be a short list");
      out.alternatives = clone(v.alternatives);
    }
    return out;
  };
  const altIndex = (v, p, field) => {
    if (typeof v !== "number" || !Number.isInteger(v) || v < 0 || !p || v >= p.alternatives.length) fail("bad-args", `${field} must name an alternative of proposal ${p?.n ?? "?"}`);
    return v;
  };
  const proposalOf = (v, n, now) => {
    if (!isObj(v) || !Array.isArray(v.alternatives)) return fail("bad-args", "proposal {summary?, alternatives[]} is required");
    const alts = v.alternatives;
    if (alts.length < 1 || alts.length > C.proposal.maxAlternatives) fail("bad-args", `a proposal has 1..${C.proposal.maxAlternatives} alternatives`);
    for (const a of alts) {
      if (!isObj(a)) fail("bad-args", "an alternative is an object");
      const s = a.summary;
      if (s !== void 0 && (typeof s !== "string" || s.length > C.limits.summary)) fail("bad-args", `an alternative summary is a string of at most ${C.limits.summary}`);
    }
    if (v.summary !== void 0 && (typeof v.summary !== "string" || v.summary.length > C.limits.summary)) fail("bad-args", `summary is a string of at most ${C.limits.summary}`);
    return { n, ...typeof v.summary === "string" ? { summary: v.summary } : {}, alternatives: clone(alts), at: now };
  };
  function decideOrThrow(verb, payload, state, actor, ctx) {
    if (!VERBS.includes(verb)) fail("unknown-verb", `${verb} is not a v2 verb (${VERBS.join(", ")})`);
    if (!isObj(payload)) fail("bad-args", "payload must be an object");
    if (!actor || !(actor.role in C.actors)) fail("forbidden-actor", `unknown role ${String(actor?.role)}`);
    const now = ctx.now;
    const by = byOf(actor);
    const writes = { request: null, tasks: {}, ink: {} };
    const result = { verb };
    if (verb === "dispatch") {
      if (state.request) fail("illegal-transition", `request ${state.request.trace_id} exists`);
      if (!C.verbs.dispatch.actors.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not dispatch`);
      const trace = payload.trace_id;
      if (typeof trace !== "string" || !TRACE_RE.test(trace)) fail("bad-args", "trace_id (traceIdV2(project, call id)) is required");
      const ink = payload.ink;
      if (!Array.isArray(ink) || ink.length < C.limits.ink.min || ink.length > C.limits.ink.max || ink.some((x) => typeof x !== "string" || !INK_RE.test(x)) || new Set(ink).size !== ink.length) {
        fail("bad-args", `ink must be ${C.limits.ink.min}..${C.limits.ink.max} distinct markup ids`);
      }
      const ids = ink;
      const known = state.ink ?? {};
      const missing = ids.filter((id) => !known[id]?.exists);
      if (missing.length) fail("not-found", `no markup ${missing.join(", ")}`);
      const owned = ids.filter((id) => {
        const k = known[id];
        const owner = k.owner_phase;
        return typeof k.request_id === "string" && k.request_id !== "" && k.request_id !== trace && !(owner && C.phases[owner]?.inkFree);
      });
      if (owned.length) fail("owned", `${owned.join(", ")} already belong to a request -- Release it first`);
      if (typeof payload.documentId !== "string" || !payload.documentId) fail("bad-args", "documentId is required");
      if (typeof payload.page !== "number" || !Number.isInteger(payload.page) || payload.page < 0) fail("bad-args", "page must be an integer >= 0");
      const scope = payload.scope ?? C.scopes[0];
      if (typeof scope !== "string" || !C.scopes.includes(scope)) fail("bad-args", `scope must be ${C.scopes.join(" | ")}`);
      const number = (Number.isInteger(state.lastNumber) && state.lastNumber > 0 ? state.lastNumber : 0) + 1;
      const t = trace;
      const draft = { id: draftIdV2(t, 0), kind: "draft", payload: {}, attempts: 0, state: "ready", seq: 0, updated_at: now };
      const r2 = {
        trace_id: t,
        requestVersion: C.requestVersion,
        number,
        ink: [...ids],
        documentId: payload.documentId,
        page: payload.page,
        scope,
        phase: "queued",
        proposals: [],
        thread: [],
        drafts: [draft],
        counters: { drafts: 1, waits: 0 },
        created_by: by,
        createdAt: now,
        updated_at: now
      };
      r2.phase = phaseOf2(r2, []);
      for (const id of ids) writes.ink[id] = t;
      writes.request = r2;
      writes.number = number;
      result.trace_id = t;
      result.number = number;
      return { writes, result };
    }
    if (!state.request) return fail("not-found", "no request");
    const r = clone(state.request);
    const tasks = state.tasks.map(clone);
    const changed = /* @__PURE__ */ new Set();
    const touch = (t, moved = true) => {
      if (moved) t.updated_at = now;
      if (moved && C.taskStates[t.state]?.terminal && !t.ended_at) t.ended_at = now;
      if (C.kinds[t.kind]?.home !== "request") changed.add(t.id);
    };
    const entry = (type, extra = {}) => r.thread.push({ type, by, at: now, ...extra });
    const newDraft = (leased) => {
      const d = { id: draftIdV2(r.trace_id, r.counters.drafts), kind: "draft", payload: {}, attempts: 0, state: "ready", seq: r.counters.drafts };
      r.counters.drafts += 1;
      if (leased) {
        d.state = "leased";
        d.attempts = 1;
        d.lease = { holder: leased, token: ctx.token, until: untilOf(now) };
      }
      touch(d);
      r.drafts.push(d);
      return d;
    };
    const openWaiting = (kind, p) => {
      r.waiting = { kind, id: waitingIdV2(r.trace_id, r.counters.waits), payload: p };
      r.counters.waits += 1;
    };
    const dropStage = () => {
      delete r.stage;
      delete r.progress_text;
    };
    const phase = r.phase;
    const findTask = () => {
      const id = payload.task;
      if (typeof id !== "string" || !id) return fail("bad-args", "task (the task id) is required");
      const t = r.drafts.find((x) => x.id === id) ?? tasks.find((x) => x.id === id);
      if (!t) return fail("not-found", `request ${r.trace_id} has no task ${id}`);
      return t;
    };
    const kindOf = (t) => {
      const k = C.kinds[t.kind];
      if (!k) return fail("bad-args", `unknown kind ${t.kind}`);
      if (k.reserved) return fail("not-enabled", `kind ${t.kind} is reserved: ${k.reserved}`);
      return k;
    };
    switch (verb) {
      case "task.claim": {
        const t = findTask();
        const k = kindOf(t);
        const V = C.verbs["task.claim"];
        const take = V.from.includes(t.state);
        const takeover = !take && !!V.takeover && V.takeover.from.includes(t.state);
        if (!take && !takeover) fail("illegal-transition", `task ${t.id} is ${t.state}; nothing to claim`);
        if (!k.claim.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not claim a ${t.kind} task`);
        const worker = payload.worker;
        if (typeof worker !== "string" || !WORKER_RE.test(worker)) fail("bad-args", "worker (the worker id) is required to take a lease");
        if (takeover && t.lease && !expired(t.lease, now) && t.lease.holder !== worker) fail("lease-held", `held by ${t.lease.holder} until ${t.lease.until}`);
        if (take) t.attempts += 1;
        t.state = "leased";
        t.lease = { holder: worker, token: ctx.token, until: untilOf(now) };
        if (take && payload.fingerprint_before !== void 0) {
          if (typeof payload.fingerprint_before !== "string") fail("bad-args", "fingerprint_before must be a string");
          t.fingerprint_before = payload.fingerprint_before;
        }
        if (payload.describe !== void 0) fail("not-enabled", `describe digest is reserved: ${C.reserved.agents}`);
        touch(t);
        result.task = t.id;
        result.token = t.lease.token;
        result.until = t.lease.until;
        result.attempts = t.attempts;
        if (takeover) {
          result.takeover = true;
          if (k.recover) result.recover = k.recover;
        }
        break;
      }
      case "task.beat":
      case "task.complete": {
        const t = findTask();
        const k = kindOf(t);
        if (!C.verbs[verb].from.includes(t.state)) fail("lease-lost", `task ${t.id} is ${t.state}; its lease is gone -- stop`);
        if (!k.claim.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not ${verb} a ${t.kind} task`);
        if (!t.lease || typeof payload.token !== "string" || payload.token !== t.lease.token) fail("lease-lost", `the ${t.kind} lease is ${t.lease ? `held by ${t.lease.holder} under another token` : "not held"}; stop and re-claim`);
        if (verb === "task.beat") {
          t.lease.until = untilOf(now);
          if (t.kind === "draft") {
            if (payload.stage !== void 0) {
              if (typeof payload.stage !== "string" || !C.stages.includes(payload.stage)) fail("bad-args", `stage must be ${C.stages.join(" | ")}`);
              r.stage = payload.stage;
            }
            if (payload.progress_text !== void 0) {
              if (typeof payload.progress_text !== "string" || payload.progress_text.length > C.limits.progress) fail("bad-args", `progress_text is a string of at most ${C.limits.progress}`);
              r.progress_text = payload.progress_text;
            }
            if (payload.reading !== void 0 && C.reading.writtenBy.verb === verb && C.reading.writtenBy.kind === t.kind) r.reading = readingOf(payload.reading);
          }
          touch(t, false);
          result.until = t.lease.until;
          break;
        }
        const holder = t.lease.holder;
        const name = payload.outcome;
        if (typeof name !== "string" || !k.outcomes[name]) fail("bad-args", `outcome must be ${Object.keys(k.outcomes).join(" | ")}`);
        let o = k.outcomes[name];
        let outcome = name;
        const label = labelOf(t);
        if (o.only === "first" && label !== "apply") fail("illegal-transition", `${outcome} applies to a first forward apply, not a ${label}`);
        for (const f of o.requires ?? []) if (payload[f] === void 0 || payload[f] === null || payload[f] === "") fail("bad-args", `${outcome} needs ${f}`);
        if (o.retry) {
          const cap = k.attempts ?? Infinity;
          if (t.attempts < cap) {
            if (o.error) t.error = errorOf(payload.error, o.error === "required");
            t.state = "ready";
            delete t.lease;
            touch(t);
            result.retry = true;
            result.outcome = outcome;
            break;
          }
          outcome = o.exhausted;
          const ex = k.outcomes[outcome];
          o = ex && !ex.retry ? ex : { state: "failed", ...o.error ? { error: o.error } : {} };
          result.exhausted = true;
        }
        const err = o.error || o.defaultError ? errorOf(payload.error, o.error === "required") ?? (o.defaultError ? { ...o.defaultError } : void 0) : void 0;
        t.state = o.state ?? "done";
        t.outcome = outcome;
        delete t.lease;
        if (err) t.error = err;
        if (payload.result !== void 0) t.result = clone(payload.result);
        if (t.kind === "render" && typeof payload.revision_after === "string") t.result = { ...isObj(t.result) ? t.result : {}, revision_after: payload.revision_after };
        touch(t);
        result.outcome = outcome;
        if (t.kind === "draft") dropStage();
        if (o.opens === "proposal") {
          const n = r.proposals.length + 1;
          r.proposals.push(proposalOf(payload.proposal, n, now));
          openWaiting("proposal", { n });
          result.n = n;
        } else if (o.opens === "question") {
          openWaiting("question", { source: t.kind, task: t.id, ...err ? { error: err } : {} });
          if (o.entry) entry(o.entry, { text: err?.user_text });
        }
        if (o.question === "first" && label === "apply") openWaiting("question", { source: t.kind, task: t.id, ...err ? { error: err } : {} });
        if (o.redraft) {
          const d = newDraft(holder);
          if (o.redraft.stage) r.stage = o.redraft.stage;
          result.draft = { task: d.id, token: d.lease.token, until: d.lease.until };
        }
        if (o.followUp) {
          const fk = C.kinds[o.followUp.kind];
          if (!fk || fk.reserved) fail("not-enabled", `follow-up kind ${o.followUp.kind} is not enabled`);
          const seq = tasks.filter((x) => x.kind === o.followUp.kind).length;
          const f = { id: opIdV2(r.trace_id, o.followUp.kind, Number(t.payload.n ?? 0), t.seq), kind: o.followUp.kind, payload: { target: o.followUp.target, op: t.id }, attempts: 0, state: "ready", seq };
          if (tasks.some((x) => x.id === f.id)) fail("illegal-transition", `task ${f.id} exists`);
          touch(f);
          tasks.push(f);
          result.followUp = f.id;
        }
        break;
      }
      case "respond": {
        const V = C.verbs.respond;
        if (r.waiting?.kind !== V.waiting) fail("illegal-transition", `request ${r.trace_id} is ${phase}: no open ${V.waiting} to respond to`);
        if (!V.actors.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not accept`);
        const w = r.waiting;
        if (payload.waiting_id !== w.id) fail("stale-version", `the open wait is ${w.id}, the call answers ${String(payload.waiting_id)}`);
        const n = w.payload.n;
        if (payload.n !== n) fail("stale-version", `the open proposal is ${n}, the call accepts ${String(payload.n)}`);
        const p = r.proposals.find((x) => x.n === n);
        const alt = altIndex(payload.accept, p, "accept");
        const a = p.alternatives[alt];
        delete r.waiting;
        entry("decision", { n, alt });
        const seq = applies(tasks).length;
        const t = {
          id: opIdV2(r.trace_id, "apply", n, seq),
          kind: "apply",
          // D16: the alternative's `copies` fields (preimage_ref included) ride on the task; task.create clones them on
          payload: { direction: "forward", alt, n, ...Object.fromEntries(V.copies.filter((f) => a[f] !== void 0).map((f) => [f, clone(a[f])])) },
          attempts: 0,
          state: "ready",
          seq
        };
        touch(t);
        tasks.push(t);
        result.task = t.id;
        break;
      }
      case "task.create": {
        const V = C.verbs["task.create"];
        if (!V.phases.includes(phase)) fail("illegal-transition", `request ${r.trace_id} is ${phase}`);
        if (!V.actors.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not create a task`);
        if (typeof payload.kind !== "string" || !V.kinds.includes(payload.kind)) {
          const k = typeof payload.kind === "string" ? C.kinds[payload.kind] : void 0;
          fail(k?.reserved ? "not-enabled" : "bad-args", `kind must be ${V.kinds.join(" | ")}`);
        }
        const dir = payload.direction;
        const rule = V.rules.find((x) => x.direction === dir);
        if (!rule) return fail("bad-args", `direction must be ${C.directions.join(" | ")}`);
        const latest = latestApply(tasks);
        const hit = rule.latest.find((e) => latest && labelOf(latest) === e.label && latest.state === e.state && (!e.rendered || renderOf(tasks, latest)?.outcome === "rendered"));
        if (!hit) fail("illegal-transition", `the latest apply of ${r.trace_id} is ${latest ? `${labelOf(latest)} ${latest.state}` : "none"}; ${dir} needs ${rule.latest.map((e) => `${e.label} ${e.state}${e.rendered ? " rendered" : ""}`).join(" | ")}`);
        const ofId = hit.of === "self" ? latest.id : latest.of;
        const src = tasks.find((x) => x.id === ofId);
        if (!src) return fail("illegal-transition", `the chain names ${String(ofId)}, which ${r.trace_id} does not hold`);
        if (rule.restorable) {
          const plan = src.payload.restore_plan;
          if (!isObj(plan)) fail("illegal-transition", `task ${src.id} has no restore plan (not restorable)`);
          if (plan.restorable !== true) fail("illegal-transition", `task ${src.id} is not restorable${typeof plan.reason === "string" ? `: ${String(plan.reason)}` : ""}`);
        }
        const n = Number(src.payload.n ?? 0);
        const seq = applies(tasks).length;
        const t = { id: "", kind: "apply", of: src.id, payload: { ...clone(src.payload), direction: dir }, attempts: 0, state: "ready", seq };
        t.id = opIdV2(r.trace_id, labelOf(t), n, seq);
        touch(t);
        tasks.push(t);
        result.task = t.id;
        result.label = labelOf(t);
        break;
      }
      case "post": {
        const type = payload.entry;
        const spec = typeof type === "string" ? C.entries[type] : void 0;
        if (!spec) return fail("bad-message", `entry must be one of ${Object.keys(C.entries).filter((e) => C.entries[e].actors).join(" | ")}`);
        if (spec.reserved) fail("not-enabled", `${String(type)} is reserved: ${spec.reserved}`);
        if (!spec.actors) fail("bad-message", `${String(type)} is written by ${spec.writtenBy ?? "the engine"}, not posted`);
        if (!(spec.phases ?? []).includes(phase)) fail("illegal-transition", `request ${r.trace_id} is ${phase}; no ${String(type)} here`);
        if (spec.notReleased && r.released) fail("illegal-transition", `request ${r.trace_id} is released`);
        if (!spec.actors.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not post a ${String(type)}`);
        const text = textOf(payload.text, !!spec.requiresText, String(type));
        const latestP = lastOf(r.proposals);
        const alt = payload.alt === void 0 ? void 0 : altIndex(payload.alt, latestP, "alt");
        let readingN;
        if (payload.reading_n !== void 0) {
          if (type !== C.reading.replacedBy.entry || typeof payload.reading_n !== "number" || !Number.isInteger(payload.reading_n)) fail("bad-args", `reading_n (an integer) names the reading a ${C.reading.replacedBy.entry} fixes`);
          readingN = payload.reading_n;
        }
        const replaces = type === C.reading.replacedBy.entry && C.reading.replacedBy.phases.includes(phase) && !!r.reading && text !== void 0;
        if (spec.resolvesWaiting || spec.appendsToDraft) {
          if (spec.resolvesWaiting && r.waiting) {
            delete r.waiting;
            newDraft();
            dropStage();
            result.resolved = true;
          } else if (!(spec.appendsToDraft && openDraft(r))) {
            fail("illegal-transition", `request ${r.trace_id} has no open wait and no open draft to take a ${String(type)}`);
          }
        }
        if (replaces) r.reading = { n: r.reading.n + 1, text };
        entry(String(type), { ...text !== void 0 ? { text } : {}, ...alt !== void 0 ? { alt, n: latestP.n } : {}, ...readingN !== void 0 ? { reading_n: readingN } : {} });
        break;
      }
      case "cancel": {
        const V = C.verbs.cancel;
        if (C.phases[phase]?.terminal) fail("illegal-transition", `request ${r.trace_id} is ${phase}`);
        if (V.notReleased && r.released) fail("illegal-transition", `request ${r.trace_id} is released (hidden with its ink); nothing to cancel`);
        const ne = netEffectOf2(r, tasks);
        const latest = ne.latest;
        const rule = V.rules.find((x) => x.phases.includes(phase) && (!x.effect || x.effect === ne.effect) && (!x.direction || latest && latest.payload.direction === x.direction));
        if (!rule) return fail("illegal-transition", ne.inFlight ? `the latest apply ${latest.id} is ${latest.state}; wait for it to finish` : `nothing to cancel at ${phase}`);
        if (!V.actors.includes(actor.role)) fail("forbidden-actor", `${actor.role} may not cancel`);
        textOf(payload.text, false, "cancel");
        if (rule.to === "released") {
          r.released = { by, at: now };
        } else {
          for (const d of r.drafts) {
            if (!isOpen(d)) continue;
            d.state = "failed";
            d.outcome = "cancelled";
            delete d.lease;
            touch(d);
          }
          delete r.waiting;
          dropStage();
          r.cancelled = { by, at: now };
          for (const id of r.ink) writes.ink[id] = null;
        }
        entry("decision", payload.text ? { text: String(payload.text) } : {});
        result.to = rule.to;
        break;
      }
      default:
        fail("unknown-verb", verb);
    }
    r.phase = phaseOf2(r, tasks);
    r.updated_at = now;
    writes.request = r;
    for (const t of tasks) if (changed.has(t.id)) writes.tasks[t.id] = t;
    result.phase = r.phase;
    if (JSON.stringify(r).length > C.limits.docBytes) fail("too-large", `request ${r.trace_id} would exceed ${C.limits.docBytes} bytes`);
    return { writes, result };
  }
  function decide2(verb, payload, state, actor, ctx) {
    try {
      const { writes, result } = decideOrThrow(verb, payload ?? {}, state, actor, ctx);
      return { ok: true, writes, result };
    } catch (e) {
      if (e instanceof Refused) return { ok: false, refusal: { code: e.code, message: e.message } };
      throw e;
    }
  }
  function allowedActions2(request, tasks, actor, now = request?.createdAt ?? (/* @__PURE__ */ new Date(0)).toISOString()) {
    const out = [];
    const ctx = { now, token: "00000000-0000-4000-8000-000000000000" };
    if (!request) {
      if (C.verbs.dispatch.actors.includes(actor.role)) out.push({ verb: "dispatch" });
      return out;
    }
    const state = { request, tasks: [...tasks] };
    const ok = (verb, payload) => decide2(verb, payload, state, actor, ctx).ok;
    const w = request.waiting;
    if (w?.kind === "proposal") {
      const p = request.proposals.find((x) => x.n === w.payload.n);
      p?.alternatives.forEach((_, i) => {
        if (ok("respond", { waiting_id: w.id, n: w.payload.n, accept: i })) out.push({ verb: "respond", accept: i });
      });
    }
    for (const e of Object.keys(C.entries)) if (C.entries[e].actors && ok("post", { entry: e, text: "x" })) out.push({ verb: "post", entry: e });
    if (ok("cancel", {})) out.push({ verb: "cancel" });
    for (const k of C.verbs["task.create"].kinds) for (const d of C.directions) if (ok("task.create", { kind: k, direction: d })) out.push({ verb: "task.create", direction: d });
    for (const t of [...request.drafts, ...tasks]) {
      if (ok("task.claim", { task: t.id, worker: actor.id })) out.push({ verb: "task.claim", task: t.id, ...t.state === "leased" ? { takeover: true } : {} });
      if (t.lease?.holder !== actor.id) continue;
      const token = t.lease.token;
      if (ok("task.beat", { task: t.id, token })) out.push({ verb: "task.beat", task: t.id });
      const k = C.kinds[t.kind];
      for (const o of Object.keys(k?.outcomes ?? {})) {
        const probe = { task: t.id, token, outcome: o, error: { kind: "probe", user_text: "probe" }, revision_after: "probe", proposal: { alternatives: [{}] } };
        if (ok("task.complete", probe)) out.push({ verb: "task.complete", task: t.id, outcome: o });
      }
    }
    return out;
  }
  return { contract: C, verbs: VERBS, decide: decide2, allowedActions: allowedActions2, phaseOf: phaseOf2, netEffectOf: netEffectOf2, cardOf: cardOf2, labelOf, isResting };
}
function applyWrites(state, writes) {
  const tasks = state.tasks.map((t) => writes.tasks[t.id] ?? t);
  for (const [id, t] of Object.entries(writes.tasks)) if (!tasks.some((x) => x.id === id)) tasks.push(t);
  const request = writes.request ?? state.request;
  const ink = state.ink ? { ...state.ink } : void 0;
  if (ink) {
    for (const [id, v] of Object.entries(writes.ink)) ink[id] = { ...ink[id] ?? { exists: true }, request_id: v, owner_phase: v ? request?.phase ?? null : null };
    if (request) {
      for (const id of request.ink) if (ink[id]?.request_id === request.trace_id) ink[id] = { ...ink[id], owner_phase: request.phase };
    }
  }
  return { request, tasks, ...ink ? { ink } : {}, lastNumber: writes.number ?? state.lastNumber };
}
var ENGINE_V2 = /* @__PURE__ */ makeEngine(REQUEST_CONTRACT_V2);
var V2_PHASES = /* @__PURE__ */ (() => Object.keys(REQUEST_CONTRACT_V2.phases))();
var V2_VERBS = /* @__PURE__ */ (() => ENGINE_V2.verbs)();
var V2_READS = /* @__PURE__ */ (() => Object.keys(REQUEST_CONTRACT_V2.reads).filter((k) => !k.startsWith("$") && k !== "taskRequestField"))();
var decide = (verb, payload, state, actor, ctx) => ENGINE_V2.decide(verb, payload, state, actor, ctx);
var allowedActions = (request, tasks, actor, now) => ENGINE_V2.allowedActions(request, tasks, actor, now);
var phaseOf = (request, tasks) => ENGINE_V2.phaseOf(request, tasks);
var netEffectOf = (request, tasks) => ENGINE_V2.netEffectOf(request, tasks);
var cardOf = (request, tasks) => ENGINE_V2.cardOf(request, tasks);

// src/markups/reviewRecord.ts
var isObj2 = (v) => typeof v === "object" && v !== null && !Array.isArray(v);
function sortDeep(v) {
  if (Array.isArray(v)) return v.map((x) => x === void 0 ? null : sortDeep(x));
  if (v instanceof Date) return v.toISOString();
  if (isObj2(v)) {
    const out = {};
    for (const k of Object.keys(v).sort()) if (v[k] !== void 0) out[k] = sortDeep(v[k]);
    return out;
  }
  return v;
}
var LS = new RegExp(String.fromCharCode(8232), "g");
var PS = new RegExp(String.fromCharCode(8233), "g");
function canonicalJson(value) {
  return JSON.stringify(sortDeep(value)).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026").replace(LS, "\\u2028").replace(PS, "\\u2029");
}
function validTraceId(value) {
  return typeof value === "string" && new RegExp(REVIEW_TRACE.pattern).test(value) ? value : void 0;
}
function upgradeTraceId(primaryId) {
  return `tr-up-${primaryId.replace(/[^A-Za-z0-9_-]/g, "_")}`.slice(0, 64);
}
function requestFromDelta(d) {
  const r = { driver: d.driver, verb: d.verb, args: d.args ?? {} };
  if (d.preimage !== void 0) r.preimage = d.preimage;
  if (d.base_package_hash !== void 0) r.base_package_hash = d.base_package_hash;
  return r;
}
function reviewFromDelta(d, traceId) {
  const r = { summary: d.summary ?? "" };
  if (d.base_rev !== void 0) r.base_rev = d.base_rev;
  if (d.base_revs !== void 0) r.base_revs = d.base_revs;
  if (d.alternatives !== void 0) {
    r.alternatives = d.alternatives.map((a) => ({ summary: a.summary, request: { driver: a.delta.driver, verb: a.delta.verb, args: a.delta.args }, confidence: a.confidence }));
  }
  if (d.selected !== void 0) r.selected = d.selected;
  if (d.revision !== void 0) r.revision = d.revision;
  if (d.revision_of !== void 0) r.revision_of = d.revision_of;
  if (d.previous_summary !== void 0) r.previous_summary = d.previous_summary;
  r.trace_id = traceId;
  return r;
}
function reviewRecordOf(doc, id = "") {
  if (!doc) return null;
  if (isObj2(doc.request) && isObj2(doc.review)) {
    return { source: "v7", request: doc.request, review: doc.review };
  }
  if (isObj2(doc.delta)) {
    const d = doc.delta;
    const trace = validTraceId(d.trace_id) ?? upgradeTraceId(String(doc.in_reply_to || id || "unknown"));
    const review = reviewFromDelta(d, trace);
    if (d.hash !== void 0) review.hash = d.hash;
    return { source: "legacy", request: requestFromDelta(d), review };
  }
  return null;
}
function deltaView(doc) {
  if (!doc) return void 0;
  if (isObj2(doc.delta) && !(isObj2(doc.request) && isObj2(doc.review))) return doc.delta;
  const rec = reviewRecordOf(doc);
  if (!rec) return void 0;
  const { request: q, review: r } = rec;
  const d = { driver: q.driver, verb: q.verb, args: q.args, summary: r.summary };
  if (q.preimage !== void 0) d.preimage = q.preimage;
  if (r.base_rev !== void 0) d.base_rev = r.base_rev;
  if (r.base_revs !== void 0) d.base_revs = r.base_revs;
  if (q.base_package_hash !== void 0) d.base_package_hash = q.base_package_hash;
  if (r.hash !== void 0) d.hash = r.hash;
  if (r.alternatives !== void 0) {
    d.alternatives = r.alternatives.map((a) => ({ summary: a.summary, delta: { ...a.request }, confidence: a.confidence }));
  }
  if (r.selected !== void 0) d.selected = r.selected;
  if (r.revision !== void 0) d.revision = r.revision;
  if (r.revision_of !== void 0) d.revision_of = r.revision_of;
  if (r.previous_summary !== void 0) d.previous_summary = r.previous_summary;
  d.trace_id = r.trace_id;
  return d;
}

// src/constants.ts
var CURRENT_FORMAT_VERSION = "1.13";
var CURRENT_CLASS_REGISTRY = "1.0";
var ELEMENT_CLASSES = [
  "wall",
  "curtain-wall",
  "slab",
  "floor",
  "column",
  "beam",
  "brace",
  "footing",
  "roof",
  "door",
  "window",
  "curtain-panel",
  "curtain-mullion",
  "ceiling",
  "room",
  "stair",
  "ramp",
  "furniture",
  "casework",
  "equipment",
  "parcel",
  "zoning-envelope",
  "mass",
  "other"
];
var SIDECAR_NAMES = [
  "elements.json",
  "marks.json",
  "scales.json",
  "levels.json",
  "titleblock.json",
  "annotations.json",
  "model3d.json",
  "schedules.json",
  "ifc-subset.json",
  // v1.12: optional page manifest (page_id + per-page source). No dependencies;
  // appended last so existing positions do not move.
  "pages.json"
];

// src/coordinates.ts
function normalizePageRotation(rotation) {
  if (!Number.isFinite(rotation)) return 0;
  const normalized = (Math.round(rotation) % 360 + 360) % 360;
  switch (normalized) {
    case 0:
    case 90:
    case 180:
    case 270:
      return normalized;
    default:
      return 0;
  }
}
function pageBoxFromRect(rect) {
  const x0 = Math.min(rect[0], rect[2]);
  const y0 = Math.min(rect[1], rect[3]);
  const x1 = Math.max(rect[0], rect[2]);
  const y1 = Math.max(rect[1], rect[3]);
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}
function visiblePageBox(mediaBox, cropBox) {
  if (!cropBox) return { ...mediaBox };
  const x0 = Math.max(mediaBox.x, cropBox.x);
  const y0 = Math.max(mediaBox.y, cropBox.y);
  const x1 = Math.min(mediaBox.x + mediaBox.width, cropBox.x + cropBox.width);
  const y1 = Math.min(mediaBox.y + mediaBox.height, cropBox.y + cropBox.height);
  if (x1 <= x0 || y1 <= y0) return { ...mediaBox };
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}
function pageViewGeometry(box, rotation = 0) {
  return { box: { ...box }, rotation: normalizePageRotation(rotation) };
}
function zeroOriginGeometry(geom) {
  return { box: { x: 0, y: 0, width: geom.box.width, height: geom.box.height }, rotation: geom.rotation };
}
function canvasSize(geom) {
  const quarter = geom.rotation === 90 || geom.rotation === 270;
  return quarter ? { width: geom.box.height, height: geom.box.width } : { width: geom.box.width, height: geom.box.height };
}
function canvasToUserSpace(point, box, rotation) {
  const { x: bx, y: by, width: w, height: h } = box;
  switch (normalizePageRotation(rotation)) {
    case 90:
      return { x: bx + point.y, y: by + point.x };
    case 180:
      return { x: bx + w - point.x, y: by + point.y };
    case 270:
      return { x: bx + w - point.y, y: by + h - point.x };
    default:
      return { x: bx + point.x, y: by + h - point.y };
  }
}
function userSpaceToCanvas(point, box, rotation) {
  const { x: bx, y: by, width: w, height: h } = box;
  switch (normalizePageRotation(rotation)) {
    case 90:
      return { x: point.y - by, y: point.x - bx };
    case 180:
      return { x: bx + w - point.x, y: point.y - by };
    case 270:
      return { x: by + h - point.y, y: bx + w - point.x };
    default:
      return { x: point.x - bx, y: by + h - point.y };
  }
}
function pageRelativeToCanvas(point, geom) {
  return userSpaceToCanvas(point, zeroOriginGeometry(geom).box, geom.rotation);
}
function canvasToPageRelative(point, geom) {
  return canvasToUserSpace(point, zeroOriginGeometry(geom).box, geom.rotation);
}
function userSpaceBBoxToCanvas(bbox, geom) {
  const [x0, y0, x1, y1] = bbox;
  const corners = [
    { x: x0, y: y0 },
    { x: x1, y: y0 },
    { x: x1, y: y1 },
    { x: x0, y: y1 }
  ].map((p) => userSpaceToCanvas(p, geom.box, geom.rotation));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  return { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) };
}

// src/consistency.ts
function metaOf(doc) {
  const m = doc?.meta;
  if (!m || typeof m !== "object") return void 0;
  return m;
}
function sameExport(a, b) {
  return a.document_id === b.document_id && a.exported_at === b.exported_at;
}
function checkProvenance(docs) {
  const manifest = docs.find((d) => d.name === "pages.json")?.doc;
  if (sourceDocumentIds(manifest).length > 0) {
    return checkProvenanceBySource(manifest, docs.filter((d) => d.name !== "pages.json"));
  }
  return checkProvenancePackageWide(docs);
}
function checkProvenancePackageWide(docs) {
  const sidecars = [];
  let reference;
  for (const { name, doc } of docs) {
    const meta = metaOf(doc);
    if (meta && !reference) reference = meta;
    sidecars.push({
      name,
      hasMeta: Boolean(meta),
      document_id: meta?.document_id,
      exported_at: meta?.exported_at,
      agrees: meta && reference ? sameExport(meta, reference) : null
    });
  }
  const withMeta = sidecars.filter((s) => s.hasMeta);
  if (withMeta.length < 2) {
    return {
      verdict: "unverifiable",
      message: withMeta.length === 0 ? `no sidecar carries meta -- provenance cannot be checked (pre-1.6 package?)` : `only ${withMeta[0].name} carries meta -- nothing to compare it against`,
      reference,
      sidecars
    };
  }
  const bad = withMeta.filter((s) => s.agrees === false);
  if (bad.length > 0) {
    return {
      verdict: "drifted",
      message: `${bad.length} of ${withMeta.length} sidecars declare a different export than ${withMeta[0].name} (${reference?.document_id} @ ${reference?.exported_at}): ` + bad.map((s) => `${s.name} (${s.document_id} @ ${s.exported_at})`).join(", "),
      reference,
      sidecars
    };
  }
  return {
    verdict: "bound",
    message: `${withMeta.length} sidecars agree on export ${reference?.document_id} @ ${reference?.exported_at}`,
    reference,
    sidecars
  };
}
function sourcesOfSidecar(name, doc, manifest) {
  const ids = /* @__PURE__ */ new Set();
  for (const { page } of recordPagesOf(name, doc)) {
    const id = pageSourceOf(manifest, page)?.document_id;
    if (id) ids.add(id);
  }
  if (name === "elements.json") {
    const elements = doc?.elements;
    if (Array.isArray(elements)) {
      for (const el of elements) {
        const id = el && typeof el === "object" ? elementScope(el, manifest) : void 0;
        if (id) ids.add(id);
      }
    }
  }
  return ids;
}
function checkProvenanceBySource(manifest, docs) {
  const sourceIds = sourceDocumentIds(manifest);
  const known = new Set(sourceIds);
  const problems = [];
  const firstPageOf = /* @__PURE__ */ new Map();
  for (const p of manifest.pages) {
    const s = p?.source;
    if (!s?.document_id) continue;
    const first = firstPageOf.get(s.document_id);
    if (!first) {
      firstPageOf.set(s.document_id, { page: p.page, exported_at: s.exported_at, revision: s.revision });
    } else if (first.exported_at !== s.exported_at || first.revision !== s.revision) {
      problems.push(
        `pages.json page ${p.page} (${s.document_id} @ ${s.exported_at}) disagrees with page ${first.page} (@ ${first.exported_at}) of the same source`
      );
    }
  }
  const sidecars = [];
  const groups = /* @__PURE__ */ new Map();
  let compared = 0;
  for (const { name, doc } of docs) {
    const meta = metaOf(doc);
    const spanned = sourcesOfSidecar(name, doc, manifest);
    let scope;
    if (spanned.size === 1) scope = [...spanned][0];
    else if (spanned.size === 0) {
      if (meta && known.has(meta.document_id)) scope = meta.document_id;
      else if (sourceIds.length === 1) scope = sourceIds[0];
    }
    const entry = {
      name,
      hasMeta: Boolean(meta),
      document_id: meta?.document_id,
      exported_at: meta?.exported_at,
      agrees: null,
      ...scope ? { source_document_id: scope } : {},
      ...spanned.size > 1 ? { spans_sources: [...spanned] } : {}
    };
    sidecars.push(entry);
    if (!scope) continue;
    let group = groups.get(scope);
    if (!group) {
      group = { document_id: scope, sidecars: [] };
      groups.set(scope, group);
    }
    group.sidecars.push(name);
    if (!meta) continue;
    let agrees = true;
    let judged = false;
    if (!group.reference) {
      group.reference = meta;
    } else {
      judged = true;
      if (!sameExport(meta, group.reference)) {
        agrees = false;
        problems.push(
          `${name} (${meta.document_id} @ ${meta.exported_at}) declares a different export than ${group.sidecars[0]} (${group.reference.document_id} @ ${group.reference.exported_at}) for source ${scope}`
        );
      }
    }
    const src = firstPageOf.get(scope);
    if (meta.document_id === scope && src) {
      judged = true;
      if (meta.exported_at !== src.exported_at) {
        agrees = false;
        problems.push(
          `${name} (@ ${meta.exported_at}) disagrees with pages.json source ${scope} (@ ${src.exported_at})`
        );
      }
    }
    if (judged) {
      compared += 1;
      entry.agrees = agrees;
    }
  }
  for (const s of sidecars) {
    if (s.agrees !== null || !s.hasMeta || !s.source_document_id) continue;
    const judgedPeer = sidecars.some(
      (o) => o !== s && o.source_document_id === s.source_document_id && o.agrees !== null
    );
    if (judgedPeer) s.agrees = true;
  }
  const sources = [...groups.values()];
  const reference = sources.find((g) => g.reference)?.reference;
  if (problems.length > 0) {
    return {
      verdict: "drifted",
      message: `per-source provenance drift: ${problems.join("; ")}`,
      reference,
      sidecars,
      sources
    };
  }
  if (compared === 0) {
    return {
      verdict: "unverifiable",
      message: `pages.json lists ${sourceIds.length} source(s); no source has two sidecar metas (or a meta naming the source) to compare -- per-source provenance cannot be checked`,
      reference,
      sidecars,
      sources
    };
  }
  return {
    verdict: "bound",
    message: `sidecar metas agree within each of ${sources.length} source(s) (${compared} comparison(s))`,
    reference,
    sidecars,
    sources
  };
}
function checkSheetBinding(marks, titleblock, scales) {
  const empty = { mismatches: [], repair: {} };
  if (!titleblock?.sheets?.length) {
    return { verdict: "unverifiable", message: "titleblock.json absent/empty -- nothing to check against", ...empty };
  }
  const sheetByPage = /* @__PURE__ */ new Map();
  const pageBySheet = /* @__PURE__ */ new Map();
  for (const sh of titleblock.sheets) {
    if (typeof sh.page !== "number" || !sh.sheet_number) continue;
    sheetByPage.set(sh.page, sh.sheet_number);
    pageBySheet.set(sh.sheet_number, sh.page);
  }
  const counts = /* @__PURE__ */ new Map();
  const add = (page, sheet, source) => {
    const key = `${page}\0${sheet}`;
    let hit = counts.get(key);
    if (!hit) {
      hit = { page, sheet, claims: /* @__PURE__ */ new Map() };
      counts.set(key, hit);
    }
    hit.claims.set(source, (hit.claims.get(source) ?? 0) + 1);
  };
  let tagged = 0;
  for (const m of marks?.marks ?? []) {
    if (typeof m.sheet_number !== "string" || !m.sheet_number) continue;
    if (typeof m.page !== "number") continue;
    add(m.page, m.sheet_number, "marks");
    tagged += 1;
  }
  for (const [pageKey, entry] of Object.entries(scales?.pages ?? {})) {
    const sheet = entry.sheet_number;
    if (typeof sheet !== "string" || !sheet) continue;
    const page = Number(pageKey);
    if (!Number.isInteger(page)) continue;
    add(page, sheet, "scales");
    tagged += 1;
  }
  if (tagged === 0) {
    return {
      verdict: "unverifiable",
      message: "no mark or scale page carries sheet_number -- page binding cannot be corroborated (pre-1.7 producer, or a producer with no sheet identity)",
      ...empty
    };
  }
  const mismatches = [];
  const repair = {};
  for (const { page, sheet, claims } of counts.values()) {
    const expected = sheetByPage.get(page);
    if (expected === void 0) continue;
    if (expected === sheet) continue;
    mismatches.push({
      page,
      expected_sheet: expected,
      claimed_sheet: sheet,
      claims: [...claims].map(([source, count]) => ({ source, count }))
    });
    const actual = pageBySheet.get(sheet);
    if (actual !== void 0) repair[sheet] = actual;
  }
  if (mismatches.length > 0) {
    return {
      verdict: "drifted",
      message: `${mismatches.length} page(s) disagree with titleblock -- pages were likely reordered after export. ` + mismatches.map((m) => {
        const who = m.claims.map((c) => c.source === "marks" ? `${c.count} mark(s)` : "scales").join(" + ");
        return `page ${m.page}: titleblock says ${m.expected_sheet}, ${who} claim ${m.claimed_sheet}`;
      }).join("; "),
      mismatches,
      repair
    };
  }
  return {
    verdict: "bound",
    message: `${tagged} sheet-tagged entr(ies) across ${counts.size} page(s) agree with titleblock`,
    ...empty
  };
}
var MSG_MAX_IDS = 5;
function formatDanglingIds(ids) {
  if (ids.length <= MSG_MAX_IDS) return ids.join(", ");
  return `${ids.slice(0, MSG_MAX_IDS).join(", ")} and ${ids.length - MSG_MAX_IDS} more`;
}
function checkScheduleRefs(schedules, elements, pages) {
  if (!schedules?.schedules?.length) {
    return { verdict: "skipped", message: "no schedules to check", errors: [], warnings: [], skipped_schedules: [] };
  }
  if (!elements?.elements) {
    return {
      verdict: "skipped",
      message: "elements.json absent -- cross-sidecar ref check skipped",
      errors: [],
      warnings: [],
      skipped_schedules: []
    };
  }
  const byKey = indexElements(elements.elements, pages);
  const resolves = (page, id) => byKey.has(pagedElementKey(page, id, pages));
  const classKey = (documentId, cls) => `${documentId ?? ""}\0${cls}`;
  const countByClass = /* @__PURE__ */ new Map();
  for (const el of elements.elements) {
    const k = classKey(elementScope(el, pages), el.class);
    countByClass.set(k, (countByClass.get(k) ?? 0) + 1);
  }
  const scopeOfPage = (page) => sourceDocumentIds(pages).length > 0 ? pageSourceOf(pages, page)?.document_id : void 0;
  const errors = [];
  const warnings = [];
  const skipped_schedules = [];
  for (const sched of schedules.schedules) {
    const name = sched.name;
    if (!sched.category) {
      skipped_schedules.push({ schedule_name: name, category_hint: sched.category_hint, reason: "out_of_scope" });
      continue;
    }
    if (Array.isArray(sched.excluded_element_ids) && sched.excluded_element_ids.length > 0) {
      const dangling = sched.excluded_element_ids.filter((id) => !resolves(sched.page, id));
      if (dangling.length > 0) {
        errors.push({ schedule_name: name, field: "excluded_element_ids", dangling });
      }
    }
    if (Array.isArray(sched.member_element_ids) && sched.member_element_ids.length > 0) {
      const dangling = sched.member_element_ids.filter((id) => !resolves(sched.page, id));
      if (dangling.length > 0) {
        errors.push({ schedule_name: name, field: "member_element_ids", dangling });
      }
    }
    if (typeof sched.excluded_element_count === "number") {
      const scheduleTotal = sched.member_element_ids.length + sched.excluded_element_count;
      const elementsCount = countByClass.get(classKey(scopeOfPage(sched.page), sched.category)) ?? 0;
      if (scheduleTotal !== elementsCount) {
        warnings.push({
          schedule_name: name,
          category: sched.category,
          schedule_total: scheduleTotal,
          elements_count: elementsCount
        });
      }
    }
  }
  if (errors.length > 0) {
    const summary = errors.map(
      (e) => `${e.schedule_name}: ${e.dangling.length} dangling ${e.field} (${formatDanglingIds(e.dangling)})`
    ).join("; ");
    return { verdict: "invalid", message: summary, errors, warnings, skipped_schedules };
  }
  return {
    verdict: "valid",
    message: warnings.length > 0 ? `all ids resolve; ${warnings.length} category-count mismatch(es)` : "all ids resolve",
    errors,
    warnings,
    skipped_schedules
  };
}

// src/formatVersion.ts
function checkFormatVersion(doc) {
  const version = doc?.["bim-pdf"];
  if (typeof version !== "string") {
    return { ok: false, version: null, message: 'missing or non-string "bim-pdf" version field' };
  }
  const declaredMajor = version.split(".")[0];
  const supportedMajor = CURRENT_FORMAT_VERSION.split(".")[0];
  if (declaredMajor !== supportedMajor) {
    return {
      ok: false,
      version,
      message: `format version ${version} incompatible with supported ${CURRENT_FORMAT_VERSION} (major mismatch)`
    };
  }
  return { ok: true, version, message: "ok" };
}
export {
  ANNOTATION_TYPES,
  CANVAS_ONLY_MARKUP_TYPES,
  CANVAS_TO_ANNOTATION_TYPE,
  CURRENT_CLASS_REGISTRY,
  CURRENT_FORMAT_VERSION,
  DEFAULT_REVIEW_STATUS,
  DISPATCH_LOCK,
  ELEMENT_CLASSES,
  ENGINE_V2,
  FRESH_REVIEW_STATUS,
  LEGACY_REVIEW_FIELDS,
  MARKUP_MOVE_CONSTRAINTS,
  MARKUP_TYPES,
  MEDIA_MAX_ITEMS,
  MEDIA_MIMES,
  MEDIA_SOURCE_KINDS,
  OP_INITIAL_STATUS,
  OP_STATUSES,
  REQUEST_CONTRACT,
  REQUEST_CONTRACT_V2,
  REQUEST_EDITING_STATUS,
  REQUEST_INITIAL_STATUS,
  REQUEST_STATUSES,
  REQUEST_VERBS,
  REVIEW_NOTE_MAX_LENGTH,
  REVIEW_SELECTION,
  REVIEW_STATUSES,
  REVIEW_TRACE,
  ROOM_SOURCES,
  SIDECAR_NAMES,
  V2_PHASES,
  V2_READS,
  V2_VERBS,
  allowedActions,
  applyWrites,
  canonicalJson,
  canvasSize,
  canvasToPageRelative,
  canvasToUserSpace,
  cardOf,
  cardStateOfRequest,
  checkFormatVersion,
  checkPackagePages,
  checkPagesInvariants,
  checkProvenance,
  checkScheduleRefs,
  checkSheetBinding,
  decide,
  deltaView,
  elementKey,
  elementScope,
  findRequestMove,
  indexElements,
  isDispatchLocked,
  isFlatPageScale,
  latestOpOf,
  makeEngine,
  netEffectOf,
  normalizePageRotation,
  normalizePageScales,
  opIdV2,
  pageBoxFromRect,
  pageEntryOf,
  pageIdOf,
  pageRelativeToCanvas,
  pageSourceOf,
  pageViewGeometry,
  pagedElementKey,
  phaseOf,
  recordPagesOf,
  recover,
  requestOpId,
  requestOwnsInk,
  requestTraceId,
  reviewRecordOf,
  scopedElementKey,
  sourceDocumentIds,
  traceIdV2,
  userSpaceBBoxToCanvas,
  userSpaceToCanvas,
  visiblePageBox,
  zeroOriginGeometry
};
//# sourceMappingURL=index.js.map